/* DRIVE IQ — ADVENTURE MODE V605.7 PREMIUM TOP PICKS EXPERIENCE
   File: adventure-ui.js

   V605.7 premium results presentation:
   - Adds one premium Top Picks header to the existing committed-results shelf.
   - Uses the authoritative results.count value for the sheet title.
   - Adds a working close control that returns to the map without clearing pins/results.
   - Marks the first committed result card as the featured visual card.
   - Keeps the permanent Places pill and existing scan/result ownership intact.
   - Does not create, reorder, rerank, or replace result data.
   - Does not change provider, scan, photo, routing, destination, or pin authority.

   V605.6 focused repair:
   - DESTINATIONS is now an open-only control: every activation opens/reasserts the top menu.
   - Removes the 900 ms tap suppression that could make legitimate taps appear dead.
   - Clears the results-panel guard before opening so delayed map-first cleanup cannot fight the user.
   - Results-panel collapse and its 15-second guard are blocked while Destinations owns the menu.
   - Touch-authority recovery no longer rewrites independentMenuOpen from incidental DOM display state.
   - Leaves Places, committed results, pins, selected place, scan/provider/ranking/photo/routing ownership untouched.

   V605.2 focused repair:
   - Preserves the stable V605.1 Destinations and Places behavior unchanged.
   - Hides the floating Places pill while the Ask Adventure IQ sheet is open.
   - Restores the Places pill automatically when Voice IQ closes.
   - Does not clear results, pins, shelf state, selected place, or destination state.

   V605.1 focused repair:
   - Restores the stable V604.9 Destinations open/close authority unchanged.
   - Keeps the browse-guard suppression that stopped the flashing.
   - Constrains the reopened top menu with CSS instead of removing its required
     display/transform authority.
   - Prevents full-width stretching while preserving reliable reopening.
   - Leaves Places, shelf, pins, results, and map behavior unchanged.

   V604.6 independent overlay repair:
   - Separates the top Destinations menu from the bottom Places shelf.
   - Allows both surfaces to remain open at the same time.
   - Makes Destinations a reliable open/close toggle on every tap.
   - Keeps the Places pill visible and stationary while its shelf opens or closes.
   - Removes browse-mode and destination-editing side effects from Places toggling.
   - Moves the Places pill to the lower-right map area so it cannot collide with Destinations.

   V603.4 map container protection:
   - Prevents touch-authority recovery from disabling any ancestor that contains Mapbox.
   - Protects the Adventure root and map wrappers returned by elementsFromPoint().
   - Fixes a fully visible but non-interactive Adventure map.
   - Preserves pin, Places, New Search, shelf, and Place IQ touch behavior.

   V603.3 map touch authority repair:
   - Detects invisible full-screen Adventure wrappers that remain above Mapbox after results commit.
   - Releases pointer authority for map pins, the Places pill, and New Search.
   - Uses bounded viewport hit-testing instead of a permanent MutationObserver.
   - Never disables the map, result shelf, Place IQ, compact controls, or legitimate buttons.
   - Reapplies touch authority after result commit, map restore, shelf changes, and delayed legacy repaints.
   - Preserves committed results, selected gold pins, menu reopening, and Place IQ routing.

   V603.2 persistent result-session polish:
   - Keeps the Places pill visible whenever committed results still exist.
   - Keeps the pill visible while the result shelf is open or closed.
   - Preserves the pill while reopening the Adventure menu for another search.
   - Renames Scan Again to New Search and reopens the menu without leaving Adventure Mode.
   - Preserves the current destination, pins, results, and selected gold pin until a new scan starts.
   - Restores missing result-session controls after map, shelf, and Place IQ transitions.

   V603.1 results-control polish:
   - Raises the Places pill fully above the Adventure panel minimize control.
   - Tightens the pill size and spacing for cleaner status-bar alignment.
   - Keeps Scan Again directly beneath it without entering the panel control zone.
   - Preserves map-first collapse, Browse Results, Place IQ, pins, and scan state.

   V603.0 map-first repair:
   - Automatically collapses the full Adventure setup panel after committed results.
   - Keeps the map and pins as the primary post-scan experience.
   - Moves the Places and Scan Again controls above the panel control zone.
   - Broadens panel detection so alternate wrappers cannot remain visible or block Mapbox.
   - Holds collapse authority through delayed legacy repaints until the user explicitly edits,
     opens Browse Results, opens Place IQ, or starts a replacement scan.
   - Fixes the scan-start UI reference error in hideScanningConflicts().

   V600.60 focused repair:
   - Collapses the actual visible Adventure panel and any non-map wrapper after results commit.
   - Does not rely on one element ID or one delayed callback.
   - Holds map-first results authority for five seconds while older modules finish repainting.
   - Keeps the Places and Scan Again controls visible above the map.
   - Releases the collapse immediately for destination editing, Show Menu, or a new scan.
   - Preserves Mapbox, pins, Place IQ, result memory, and result shelves.


   V600.58 focused repair:
   - Prevents the visually minimized Adventure menu from remaining as an
     invisible full-screen touch blocker over Mapbox pins.
   - Restores direct map and marker pointer authority after every result commit.
   - Keeps the Places pill visible whenever committed results exist, including
     while the compact result shelf is open.
   - Removes stale shelf-state dependency from Places-pill visibility.
   - Performs bounded post-commit interaction recovery passes.
   - Requests the compact Adventure controls shell without reopening the
     destination editor or full search menu.
   - Makes the Place IQ watchdog require a visibly rendered card rather than
     trusting a stale ui.mode === place state.
   - Preserves scan lifecycle, destination editing, Browse Results, selected
     gold pins, result memory, and all existing Place IQ routing.


   V600.56 focused repair:
   - Restores the one-time hideScanningConflicts() scan-start helper.
   - Keeps the recurring scanning interval removed.
   - Prevents scan startup from throwing before the map can transition.
   - Preserves event-driven completion, failure, cancellation, and continuation handling.
   - Removes the recurring 140 ms scanning guard entirely.
   - Scan lifecycle events now apply scanning UI changes once per event.
   - Prevents a background interval from repeatedly hiding pins, shelves,
     Places controls, menus, or restoring stale scanning presentation.
   - Keeps initial scan setup event-driven through enterScanningState().
   - Keeps completion, failure, cancellation, and continuation cleanup
     event-driven through the existing UI lifecycle handlers.
   - Separates full scans from calm continuation rounds at the UI layer.
   - Prevents refill progress from entering global scanning mode.
   - Keeps the result shelf and Places control available during Round 2 and Round 3.
   - Hides the global “Finding more trusted places” scan-status pill for continuations.
   - Restores the last usable shelf when a continuation settles, fails, times out,
     returns zero growth, or reports exhaustion.
   - Prevents the UI scanning guard from repeatedly hiding continuation results.
   - Preserves full-scan behavior, Place IQ, map mode, and remembered-market controls.

   Purpose:
   - One visual-state authority for Adventure Mode V600.
   - Owns menu, scanning, map-first, Place IQ, search, toast, and result-count UI.
   - Does not create Mapbox.
   - Does not render pins.
   - Does not execute scans, ranking, photos, or routing.
   - Never uses a MutationObserver to fight other modules.
   - Routes menu selections and pin selections into the same Place IQ card.
   - Hides the results menu before a pin-selected Place IQ transition.
   - Highlights the exact committed map pin for every menu-card selection.
   - Keeps a permanent results return control available while Place IQ is open.
   - Retires duplicate legacy SHOW MENU / Spots controls.
   - Positions one compact premium glass results control at the top-right.
   - Offsets the selected place above the Place IQ card.
   - Repeatedly suppresses recreated legacy SHOW MENU controls.
   - Keeps only the premium contextual Places control visible.
   - Persists the exact selected place through the final Place IQ transition.
   - Reapplies the gold selected-pin state after card and camera callbacks.
   - Makes the Places button restore only the V600 results shelf.
   - Prevents destination search, scan overlays, and the main menu from reopening.
   - Keeps the last selected pin gold while browsing the results shelf.
   - Treats the Places button as Browse Results, not a general UI reset.
   - Locks Browse Results as a persistent state until a new place is selected.
   - Blocks menu, destination-search, scan-overlay, and map-restore callbacks
     from replacing Browse Results.
   - Runs a bounded browse-lock heartbeat while the shelf is open.
   - Detects and suppresses legacy destination chooser overlays by structure
     and content without clearing the selected gold pin.
   - Reasserts the selected gold pin after scan commits, shelf paints, map
     recovery, Place IQ transitions, and browse-mode callbacks.
   - Shows a dedicated premium scanning status pill immediately.
   - Updates scan progress copy without blocking map interaction.
   - Hides the status pill on completion, failure, or cancellation.
   - Suppresses destination autocomplete for the entire scan lifecycle.
   - Hides stale result shelves and the legacy full-width scan overlay.
   - Keeps only the compact premium scan-status pill visible while scanning.
   - Defers persistent result-shelf ownership to adventure-scan.js.
   - Minimizes rather than destroys the shelf during Place IQ transitions.
   - Restores the exact Top Picks batch after Place IQ closes.
   - Prevents UI-level inline overrides from fighting shelf persistence.
   - Keeps the selected gold pin active while returning to results browsing.
   - Permanently suppresses stale destination suggestions after destination lock.
   - Keeps only the compact scan-status pill visible during active scans.
   - Adds smoother shelf and Places-pill transitions.
   - Raises the focused selected pin for cleaner Place IQ composition.
   - Preserves the persistent shelf and gold-pin authorities unchanged.
   - Fixes recursive destination-search closure that could overflow the stack.
   - Adds explicit scan completion state for reliable shelf restoration.
   - Removes scanning classes before restoring Top Picks.
   - Performs bounded post-completion shelf restoration passes.
   - Keeps the map and current result shelf intact until Place IQ is visibly open.
   - Never enters Place IQ mode merely because a place-opening method was called.
   - Treats a controller return value of false as a failed open attempt.
   - Removes render/select methods from the UI controller bridge so only true
     Place IQ opening methods can claim the transition.
   - Adds a bounded open watchdog that restores the map and result shelf when
     a selected result fails to produce a visible Place IQ card.
   - Prevents a failed or duplicate selection event from leaving only the
     floating Places pill over a black Adventure shell.
   - Keeps committed results without allowing them to overtake destination editing.
   - Restores the canonical committed destination label into the destination field.
   - Distinguishes placeholder/category copy from real destination input value.
   - Hides the floating Places control while the menu or destination field is active.
   - Allows explicit destination editing without destroying the committed result set.
   - Prevents Browse Results, Menu, and Destination Editing from being active together.
   - Makes destination editing an exclusive UI state that suspends all result locks.
   - Never clears the committed destination label when unlocking autocomplete.
   - Never lets existing results lock or close the destination chooser.
   - Hides the Places pill and result shelf for the entire active edit session.
   - Re-enables result browsing only after destination commit or explicit edit cancel.
   - Restores the Adventure results shell after scans without auto-opening Top Picks.
   - Keeps the map, committed pins, Adventure panel, and Places control visible together.
   - Makes the Places control a true open/close toggle through Scan V600.55.
   - Prevents scan completion from hiding every control and leaving a bare map.
   - Keeps pin taps routed directly into the exact Place IQ card.
   - Returns from a closed shelf to the same map-first results shell.
   - Adds one compact persistent Scan Again control beneath the Places control.
   - Keeps Scan Again visible whenever committed results are available on the map.
   - Restores the Adventure menu without clearing the last committed destination.
   - Lets users edit the destination, cuisine, category, city, or district immediately.
   - Keeps existing pins visible until a replacement scan actually begins.
   - Hides Scan Again while scanning, browsing Top Picks, editing, or viewing Place IQ.

   Load after:
   1. adventure-config.js
   2. adventure-utils.js
   3. adventure-state.js
   4. adventure-events.js
*/

(function (window, document) {
  "use strict";

  if (window.DriveIQAdventureUI) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;
  var state = window.DriveIQAdventureState;
  var events = window.DriveIQAdventureEvents;

  if (!config || !utils || !state || !events) {
    throw new Error(
      "adventure-ui.js requires config, utils, state, and events first."
    );
  }

  var initialized = false;
  var toastTimer = null;
  var progressTimer = null;
  var unsubscribers = [];
  var openingPlaceKey = "";
  var openingPlaceTimer = null;
  var placeOpenWatchdogTimer = null;
  var PLACE_OPEN_WATCHDOG_MS = 900;
  var legacyControlTimer = null;
  var activePlaceItem = null;
  var pinHighlightTimers = [];
  var goldReassertTimers = [];
  var lastGoldReassertAt = 0;
  var resultsReturnInProgress = false;
  var browseResultsActive = false;
  var resultsReturnTimers = [];
  var browseGuardTimer = null;
  var scanStatusTimer = null;
  var scanStatusVisible = false;
  var scanningActive = false;
  var scanCompleted = false;
  var calmContinuationActive = false;
  var calmContinuationToken = "";
  var destinationChooserLocked = false;
  var destinationChooserLockTimer = null;
  var destinationEditingActive = false;
  var destinationFieldSyncTimer = null;
  var lastCommittedDestinationLabel = "";
  var resultsPanelGuardTimer = null;
  var resultsPanelGuardUntil = 0;
  var independentMenuOpen = false;
  var persistentDestinationsTransitionUntil = 0;
  var categoryRescanActive = false;
  var categoryRescanFilter = "";
  var categoryRescanLabel = "";
  var categoryRescanFallbackTimer = null;
  var touchAuthorityTimers = [];
  var BROWSE_GUARD_INTERVAL_MS = 140;
  var CAMERA_VERTICAL_OFFSET = -215;
  var LEGACY_RESULTS_CONTROLS = [
    "adventureMenuRestore",
    "diqV528ResultsPill",
    "diqV527MapResultsPill",
    "diq-adventure-results-toggle",
    "diqV519Reopen",
    "diqV518Reopen",
    "diqV526PlacesReopen"
  ];

  var ids = {
    root: "diqAdventureV600Root",
    menu: "adventureHero",
    searchPanel: "diqAdventureSearchResults",
    sourceSearchPanel: "diqV500SearchPanel",
    legacyShelf: "diqAdventureDestResults",
    legacyShelfV519: "diqV519Shelf",
    resultShelf: "diqV600ResultShelf",
    resultShelfPlural: "diqV600ResultsShelf",
    scanOverlay: "diqAdventureScanTab",
    scanTitle: "diqScanTitle",
    scanSubtitle: "diqScanSub",
    scanStatus: "diqV600ScanStatus",
    scanStatusText: "diqV600ScanStatusText",
    scanStatusDot: "diqV600ScanStatusDot",
    resultsPill: "diqV600ResultsPill",
    scanAgain: "diqV600ScanAgain",
    persistentBack: "diqAdventurePersistentBack",
    persistentDestinations: "diqAdventurePersistentDestinations",
    categoryDock: "diqAdventureCategoryDock",
    toast: "adventureToast",
    map: config.map.containerID,
    placeCard: config.placeCard.cardID
  };

  function log() {
    if (
      !config.logging.enabled ||
      !window.console ||
      !window.console.log
    ) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift(config.logging.prefix, "[UI]");
    window.console.log.apply(window.console, args);
  }

  function get(id) {
    return utils.byId(id);
  }

  function currentMode() {
    return String(state.get("ui.mode", "menu") || "menu");
  }

  function destinationInputFields() {
    var fields = [];
    var seen = [];

    [
      "diqAdventureDestinationInput",
      "diqAdventureSearchInput",
      "diqV500SearchInput"
    ].forEach(function (id) {
      var field = get(id);

      if (field && seen.indexOf(field) < 0) {
        seen.push(field);
        fields.push(field);
      }
    });

    try {
      Array.prototype.forEach.call(
        document.querySelectorAll(
          "[data-diq-destination-input]," +
          "[data-adventure-search-input]," +
          "input[data-diq-location-search]," +
          "input[name='destination']"
        ),
        function (field) {
          if (field && seen.indexOf(field) < 0) {
            seen.push(field);
            fields.push(field);
          }
        }
      );
    } catch (error) {}

    return fields;
  }

  function destinationLabelFromState() {
    var committed =
      state.get("destination.committed", null) ||
      state.get("destination.selected", null) ||
      state.get("destination", null) ||
      null;
    var label = "";

    if (committed && typeof committed === "object") {
      label = String(
        committed.canonicalLabel ||
        committed.displayName ||
        committed.full ||
        committed.label ||
        committed.name ||
        committed.formattedAddress ||
        committed.address ||
        ""
      ).replace(/\s+/g, " ").trim();
    } else if (typeof committed === "string") {
      label = committed.replace(/\s+/g, " ").trim();
    }

    if (label) {
      lastCommittedDestinationLabel = label;
    }

    return label || lastCommittedDestinationLabel;
  }

  function fieldHasUserValue(field) {
    var value;

    if (!field) {
      return false;
    }

    value = String(field.value || "").trim();

    return !!(
      value &&
      value !== String(field.placeholder || "").trim()
    );
  }

  function syncDestinationField(reason, force) {
    var label = destinationLabelFromState();

    if (destinationFieldSyncTimer) {
      window.clearTimeout(destinationFieldSyncTimer);
      destinationFieldSyncTimer = null;
    }

    if (!label) {
      return false;
    }

    destinationInputFields().forEach(function (field) {
      var active = document.activeElement === field;
      var current = String(field.value || "").trim();

      if (
        !force &&
        active &&
        destinationEditingActive &&
        current &&
        current !== label
      ) {
        return;
      }

      field.value = label;
      field.setAttribute("value", label);
      field.setAttribute("data-diq-committed-destination", label);
      field.setAttribute("aria-label", "Destination: " + label);

      try {
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
      } catch (error) {}
    });

    try {
      events.emit("ui:destination-field-synced", {
        label: label,
        reason: String(reason || "state-sync")
      });
    } catch (error) {}

    return true;
  }

  function scheduleDestinationFieldSync(reason) {
    if (destinationFieldSyncTimer) {
      window.clearTimeout(destinationFieldSyncTimer);
    }

    destinationFieldSyncTimer = window.setTimeout(function () {
      destinationFieldSyncTimer = null;
      syncDestinationField(reason || "scheduled", false);
    }, 40);

    return true;
  }

  function beginDestinationEditing(reason) {
    releaseAdventureResultsPanel("destination-editing");
    destinationEditingActive = true;
    destinationChooserLocked = false;
    window.setTimeout(function(){refreshCategoryDockVisibility("destination-editing");},0);
    browseResultsActive = false;
    resultsReturnInProgress = false;

    stopDestinationChooserLock();
    stopBrowseGuard();
    clearResultsReturnTimers();

    if (document.body) {
      document.body.classList.remove("diq-v600-browse");
      document.body.classList.remove("diq-v600-map");
      document.body.classList.add("diq-v600-menu");
      document.body.classList.add("diq-v600-destination-editing");
    }

    if (Number(state.get("results.count", 0) || 0) > 0) {
      showResultsPill(state.get("results.count", 0));
    }
    hideAllResultShelves();
    setDisplay(get(ids.menu), true);

    try {
      if (
        window.DriveIQAdventureDestination &&
        typeof window.DriveIQAdventureDestination.beginEdit === "function"
      ) {
        window.DriveIQAdventureDestination.beginEdit(
          undefined,
          String(reason || "destination-editing")
        );
      }
    } catch (error) {}

    try {
      if (typeof state.setUIState === "function") {
        state.setUIState("destination_editing");
      }
    } catch (error) {}

    try {
      events.emit("ui:destination-editing-started", {
        reason: String(reason || "destination-editing")
      });
    } catch (error) {}

    return true;
  }

  function endDestinationEditing(reason, options) {
    var supplied = options || {};
    var restoreLabel = supplied.restoreCommittedLabel === true;

    destinationEditingActive = false;
    window.setTimeout(function(){refreshCategoryDockVisibility("destination-editing-ended");},0);

    if (document.body) {
      document.body.classList.remove("diq-v600-destination-editing");
    }

    if (restoreLabel) {
      scheduleDestinationFieldSync(
        reason || "destination-editing-ended"
      );
    }

    try {
      events.emit("ui:destination-editing-ended", {
        reason: String(reason || "destination-editing-ended"),
        restoreCommittedLabel: restoreLabel
      });
    } catch (error) {}

    return true;
  }



  function browseModeActive() {
    if (destinationEditingActive) {
      return false;
    }

    return !!(
      browseResultsActive ||
      currentMode() === "browse_results" ||
      (
        document.body &&
        document.body.classList.contains("diq-v600-browse")
      )
    );
  }

  function placeIsOpen() {
    var placeCard = get(ids.placeCard);

    return !!(
      currentMode() === "place" ||
      (
        document.body &&
        (
          document.body.classList.contains("diq-v600-place") ||
          document.body.classList.contains("diqPlaceIQMode")
        )
      ) ||
      (
        placeCard &&
        placeCard.classList.contains("diqPlaceIQOpen")
      )
    );
  }


  function scanController() {
    return window.DriveIQAdventureScan || null;
  }

  function scanStatusSnapshot() {
    var scan = scanController();

    try {
      if (
        scan &&
        typeof scan.status === "function"
      ) {
        return scan.status() || {};
      }
    } catch (error) {}

    return {};
  }

  function continuationActive(payload) {
    var supplied = payload || {};
    var status = scanStatusSnapshot();

    return !!(
      calmContinuationActive ||
      supplied.isRefill === true ||
      supplied.calmContinuation === true ||
      supplied.continuation === true ||
      supplied.refillMode ||
      status.refillInProgress === true ||
      status.calmContinuationLoadingActive === true
    );
  }

  function keepContinuationResultsVisible(reason) {
    var scan = scanController();
    var count = Number(state.get("results.count", 0) || 0);

    calmContinuationActive = true;
    scanningActive = false;
    scanCompleted = true;
    if (document.body) {
      document.body.classList.remove("diq-v600-scanning");
      document.body.classList.add("diq-v600-map");
      document.body.classList.add("diq-v600-results-shell");
    }

    hideScanStatus(0);
    setDisplay(get(ids.scanOverlay), false);
    setDisplay(get(ids.searchPanel), false);
    setDisplay(get(ids.sourceSearchPanel), false);

    releaseResultShelfOverrides();

    try {
      if (
        scan &&
        typeof scan.restoreLastUsableShelf === "function"
      ) {
        scan.restoreLastUsableShelf(
          String(reason || "calm-continuation")
        );
      } else if (
        scan &&
        typeof scan.restoreResultShelf === "function"
      ) {
        scan.restoreResultShelf(
          String(reason || "calm-continuation")
        );
      }
    } catch (error) {}

    if (count > 0) {
      showResultsPill(count);
    }

    hideScanAgainControl();
    return true;
  }

  function settleCalmContinuation(reason) {
    calmContinuationActive = false;
    calmContinuationToken = "";
    scanningActive = false;
    scanCompleted = true;
    exitScanningState();
    hideScanStatus(0);

    releaseResultShelfOverrides();

    try {
      var scan = scanController();

      if (
        scan &&
        typeof scan.restoreLastUsableShelf === "function"
      ) {
        scan.restoreLastUsableShelf(
          String(reason || "calm-continuation-settled")
        );
      } else if (
        scan &&
        typeof scan.restoreResultShelf === "function"
      ) {
        scan.restoreResultShelf(
          String(reason || "calm-continuation-settled")
        );
      }
    } catch (error) {}

    return true;
  }

  function minimizePersistentShelf(reason) {
    var scan = scanController();

    try {
      if (
        scan &&
        typeof scan.minimizeResultShelf === "function"
      ) {
        return scan.minimizeResultShelf(
          String(reason || "ui-minimize")
        ) !== false;
      }
    } catch (error) {}

    hideAllResultShelves();
    return false;
  }

  function restorePersistentShelf(reason) {
    var scan = scanController();

    if (destinationEditingActive) {
      hideAllResultShelves();
      hideResultsPill();
      return false;
    }

    releaseResultShelfOverrides();

    try {
      if (
        scan &&
        typeof scan.restoreResultShelf === "function"
      ) {
        return scan.restoreResultShelf(
          String(reason || "ui-restore")
        ) !== false;
      }

      if (
        scan &&
        typeof scan.showResultShelf === "function"
      ) {
        return scan.showResultShelf() !== false;
      }
    } catch (error) {}

    return false;
  }

  function closePersistentShelf(reason) {
    var scan = scanController();

    try {
      if (
        scan &&
        typeof scan.closeResultShelf === "function"
      ) {
        return scan.closeResultShelf(
          String(reason || "ui-close")
        ) !== false;
      }
    } catch (error) {}

    hideAllResultShelves();
    return false;
  }

  function resultShelfNodes() {
    var selectors = [
      "#" + ids.resultShelf,
      "#" + ids.resultShelfPlural,
      "#" + ids.legacyShelf,
      "#" + ids.legacyShelfV519,
      ".diqAdventureResultsShelf",
      ".diqV600ResultShelf",
      ".diqV600ResultsShelf",
      "[data-diq-results-shelf]"
    ];
    var nodes = [];
    var seen = [];

    selectors.forEach(function (selector) {
      var matches;

      try {
        matches = document.querySelectorAll(selector);
      } catch (error) {
        matches = [];
      }

      Array.prototype.forEach.call(matches, function (node) {
        if (node && seen.indexOf(node) < 0) {
          seen.push(node);
          nodes.push(node);
        }
      });
    });

    return nodes;
  }

  function hideAllResultShelves() {
    resultShelfNodes().forEach(function (shelf) {
      shelf.style.setProperty("display", "none", "important");
      shelf.style.setProperty("visibility", "hidden", "important");
      shelf.style.setProperty("opacity", "0", "important");
      shelf.style.setProperty("pointer-events", "none", "important");
      shelf.setAttribute("aria-hidden", "true");
    });

    return true;
  }

  function releaseResultShelfOverrides() {
    resultShelfNodes().forEach(function (shelf) {
      shelf.style.removeProperty("display");
      shelf.style.removeProperty("visibility");
      shelf.style.removeProperty("opacity");
      shelf.style.removeProperty("pointer-events");
    });

    return true;
  }

  function setDisplay(element, visible, displayValue) {
    if (!element) {
      return;
    }

    element.style.display = visible
      ? String(displayValue || "block")
      : "none";

    element.style.visibility = visible
      ? "visible"
      : "hidden";

    element.style.pointerEvents = visible
      ? "auto"
      : "none";

    element.setAttribute(
      "aria-hidden",
      visible ? "false" : "true"
    );
  }


  function forceOpenAdventureMenuSurface(reason) {
    var hero = get("adventureHero");
    var wrap = document.querySelector(".topWrap");
    var nodes = [wrap, hero];

    if (!hero) {
      return false;
    }

    /*
     * HTML Authority:
     * The visible Adventure search menu is #adventureHero inside .topWrap.
     * Older UI builds targeted #diqAdventureMenu, which does not exist in the
     * current modular HTML shell. Restore the real nodes directly.
     */
    nodes.forEach(function (node) {
      if (!node) {
        return;
      }

      [
        "display",
        "visibility",
        "opacity",
        "pointer-events",
        "transform",
        "max-height",
        "height"
      ].forEach(function (property) {
        node.style.removeProperty(property);
      });

      node.style.setProperty("display", "block", "important");
      node.style.setProperty("visibility", "visible", "important");
      node.style.setProperty("opacity", "1", "important");
      node.style.setProperty("pointer-events", "auto", "important");
      node.style.setProperty("transform", "none", "important");

      node.removeAttribute("inert");
      node.removeAttribute("data-diq-results-panel-collapsed");
      node.removeAttribute("data-diq-touch-blocker-released");
      node.setAttribute("aria-hidden", "false");
    });

    if (document.body) {
      document.body.classList.remove("diq-v600-place");
      document.body.classList.remove("diqPlaceIQMode");
      document.body.classList.remove("diq-v600-map");
      document.body.classList.remove("diq-v600-results-shell");
      document.body.classList.remove("diq-v600-browse");
      document.body.classList.remove("diq-v600-scanning");
      document.body.classList.add("diq-v600-menu");
      document.body.classList.add("diq-v600-destination-editing");
    }

    try {
      events.emit("ui:full-menu-surface-restored", {
        reason: String(reason || "force-open-menu"),
        target: "adventureHero"
      });
    } catch (error) {}

    return true;
  }

  function forceCloseAdventureMenuSurface(reason) {
    var hero = get("adventureHero");

    /*
     * Do not let delayed results-shell cleanup immediately hide a menu that the
     * user just opened from the persistent Destinations control. This removes
     * the visible open-close-open flash while keeping explicit user closes valid.
     */
    if (
      independentMenuOpen &&
      Date.now() < persistentDestinationsTransitionUntil &&
      String(reason || "").indexOf("persistent-destinations-close") < 0
    ) {
      return true;
    }
    var wrap = document.querySelector(".topWrap");
    var nodes = [hero, wrap];

    nodes.forEach(function (node) {
      if (!node) {
        return;
      }

      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("opacity", "0", "important");
      node.style.setProperty("pointer-events", "none", "important");
      node.style.setProperty("transform", "translateY(-120vh)", "important");
      node.setAttribute("aria-hidden", "true");
      node.setAttribute(
        "data-diq-menu-surface-closed",
        String(reason || "minimize")
      );
    });

    destinationEditingActive = false;

    if (document.body) {
      document.body.classList.remove("diq-v600-menu");
      document.body.classList.remove("diq-v600-destination-editing");
      document.body.classList.add("diq-v600-map");
      document.body.classList.add("diq-v600-results-shell");
    }

    try {
      restoreMapInteractionAuthority(
        String(reason || "minimize-touch-release")
      );
      holdMapTouchAuthority(
        String(reason || "minimize-touch-release")
      );
    } catch (error) {}

    return true;
  }


  function persistentControlStyle(control, side) {
    if (!control) {
      return false;
    }

    control.style.setProperty("position", "fixed", "important");
    control.style.setProperty("top", "51px", "important");
    control.style.setProperty(side, "14px", "important");
    control.style.setProperty("z-index", "2147483647", "important");
    control.style.setProperty("display", "flex", "important");
    control.style.setProperty("visibility", "visible", "important");
    control.style.setProperty("opacity", "1", "important");
    control.style.setProperty("pointer-events", "auto", "important");
    control.style.setProperty("align-items", "center", "important");
    control.style.setProperty("justify-content", "center", "important");
    control.style.setProperty("height", "36px", "important");
    control.style.setProperty("min-width", "36px", "important");
    control.style.setProperty("padding", side === "right" ? "0 12px" : "0", "important");
    control.style.setProperty("border-radius", "13px", "important");
    control.style.setProperty("border", "1px solid rgba(255,255,255,.18)", "important");
    control.style.setProperty("background", "rgba(5,17,28,.92)", "important");
    control.style.setProperty("box-shadow", "0 12px 30px rgba(0,0,0,.38)", "important");
    control.style.setProperty("backdrop-filter", "blur(16px)", "important");
    control.style.setProperty("-webkit-backdrop-filter", "blur(16px)", "important");
    control.style.setProperty("color", "#f4fffb", "important");
    control.style.setProperty("font", "900 11px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", "important");
    control.style.setProperty("letter-spacing", ".25px", "important");
    control.style.setProperty("text-decoration", "none", "important");
    control.style.setProperty("touch-action", "manipulation", "important");
    control.removeAttribute("inert");
    control.setAttribute("aria-hidden", "false");
    control.setAttribute("data-diq-persistent-adventure-control", "1");
    return true;
  }

  function ensurePersistentNavigationControls() {
    var originalBack = get("adventureBackBtn");
    var back = get(ids.persistentBack);
    var destinations = get(ids.persistentDestinations);

    /*
     * Keep navigation controls outside every collapsible Adventure wrapper.
     * Results-shell cleanup may hide or translate the setup panel, but it must
     * never remove the user's way out or the destination/search menu.
     */
    if (!back) {
      back = document.createElement("a");
      back.id = ids.persistentBack;
      back.href =
        originalBack && originalBack.getAttribute("href")
          ? originalBack.getAttribute("href")
          : "https://driveiqgps.com";
      back.textContent = "←";
      back.setAttribute("aria-label", "Back to Drive IQ");
      back.setAttribute("title", "Back to Drive IQ");
      document.body.appendChild(back);
    }

    if (!back.__diqPersistentBackBound) {
      back.addEventListener("click", function (event) {
        /*
         * Preserve the canonical homepage destination. The anchor href remains
         * authoritative even when JavaScript navigation is unavailable.
         */
        try {
          events.emit("ui:adventure-back-requested", {
            reason: "persistent-back"
          });
        } catch (error) {}
      }, false);
      back.__diqPersistentBackBound = true;
    }

    if (!destinations) {
      destinations = document.createElement("button");
      destinations.id = ids.persistentDestinations;
      destinations.type = "button";
      destinations.textContent = "DESTINATIONS";
      destinations.setAttribute("aria-label", "Open destinations and Adventure search");
      destinations.setAttribute("title", "Open destinations and Adventure search");
      document.body.appendChild(destinations);
    }

    if (!destinations.__diqPersistentDestinationsBound) {
      function settleIndependentDestinationsOpen(reason) {
        var hero = get("adventureHero");
        var wrap = document.querySelector(".topWrap");

        [wrap, hero].forEach(function (node) {
          if (!node) {
            return;
          }

          [
            "display",
            "visibility",
            "opacity",
            "pointer-events",
            "transform",
            "max-height",
            "height"
          ].forEach(function (property) {
            node.style.removeProperty(property);
          });

          node.style.setProperty("display", "block", "important");
          node.style.setProperty("visibility", "visible", "important");
          node.style.setProperty("opacity", "1", "important");
          node.style.setProperty("pointer-events", "auto", "important");
          node.style.setProperty("transform", "none", "important");
          node.removeAttribute("inert");
          node.removeAttribute("data-diq-touch-authority-disabled");
          node.removeAttribute("data-diq-results-panel-collapsed");
          node.setAttribute("aria-hidden", "false");
        });

        if (document.body) {
          document.body.classList.add("diq-v600-independent-menu-open");
        }

        independentMenuOpen = true;
        destinations.setAttribute("aria-expanded", "true");
        scheduleDestinationFieldSync(reason || "persistent-destinations-open");
        return true;
      }

      function handlePersistentDestinations(event) {
        var now = Date.now();

        if (event) {
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === "function") {
            event.stopImmediatePropagation();
          }
        }

        /*
         * V605.6 DESTINATIONS OPEN AUTHORITY
         * This control is intentionally open-only. Every activation means:
         * "show me Destinations now." Never interpret the same button as a close.
         * Clear delayed results-shell authority first, then reassert the real
         * Adventure menu surface. Existing Places/results/pins stay intact.
         */
        persistentDestinationsTransitionUntil = now + 1600;

        if (resultsPanelGuardTimer) {
          window.clearInterval(resultsPanelGuardTimer);
          resultsPanelGuardTimer = null;
        }
        resultsPanelGuardUntil = 0;

        browseResultsActive = false;
        resultsReturnInProgress = false;
        stopBrowseGuard();
        clearResultsReturnTimers();

        try {
          releaseAdventureResultsPanel("persistent-destinations-open");
        } catch (releaseError) {}

        if (document.body) {
          document.body.classList.remove("diq-v600-browse");
          document.body.classList.remove("diq-v600-place");
          document.body.classList.remove("diqPlaceIQMode");
        }

        settleIndependentDestinationsOpen("persistent-destinations-open");

        [0, 80, 220, 520, 1000].forEach(function (delay) {
          window.setTimeout(function () {
            if (!independentMenuOpen) {
              return;
            }
            settleIndependentDestinationsOpen(
              "persistent-destinations-open-" + String(delay)
            );
          }, delay);
        });

        if (Number(state.get("results.count", 0) || 0) > 0) {
          showResultsPill(state.get("results.count", 0));
        }
        refreshCategoryDockVisibility("persistent-destinations");

        try {
          events.emit("ui:persistent-destinations-toggled", {
            open: true,
            reason: "persistent-destinations-open"
          });
        } catch (error) {}

        return true;
      }

      /*
       * V605.6 Single Button Authority:
       * Use one capture-phase click handler. No 900 ms lockout and no
       * pointerdown/pointerup handshake that can strand the control on iOS.
       */
      destinations.addEventListener("click", handlePersistentDestinations, true);
      destinations.__diqPersistentDestinationsBound = true;
      destinations.setAttribute("aria-expanded", "false");
    }

    persistentControlStyle(back, "left");
    persistentControlStyle(destinations, "right");

    if (originalBack) {
      originalBack.style.setProperty("display", "none", "important");
      originalBack.style.setProperty("pointer-events", "none", "important");
      originalBack.setAttribute("aria-hidden", "true");
    }

    restoreBottomSwipeMenu();
    return true;
  }


  function restoreBottomSwipeMenu() {
    var restore = get("adventurePanelRestore");

    if (!restore) {
      return false;
    }

    /*
     * The permanent Destinations control now owns menu reopening.
     * Hide only the duplicate SHOW MENU button. Do not touch the bottom result
     * shelf, Place IQ card, pins, scan lifecycle, or Destinations control.
     */
    restore.style.setProperty("display", "none", "important");
    restore.style.setProperty("visibility", "hidden", "important");
    restore.style.setProperty("opacity", "0", "important");
    restore.style.setProperty("pointer-events", "none", "important");
    restore.setAttribute("aria-hidden", "true");
    restore.setAttribute("data-diq-duplicate-menu-hidden", "1");

    return true;
  }

  function ensureStyles() {
    var style;

    if (get("diqAdventureV600UIStyles")) {
      return;
    }

    style = document.createElement("style");
    style.id = "diqAdventureV600UIStyles";
    style.type = "text/css";
    style.appendChild(document.createTextNode(
      "body.diq-v600-results-shell #diqAdventureMenu," +
      "body.diq-v600-results-shell [data-diq-adventure-menu]," +
      "body.diq-v600-results-shell .diqAdventureMenu," +
      "body.diq-v600-results-shell .diq-adventure-menu," +
      "body.diq-v600-results-shell .adventure-menu-panel," +
      "body.diq-v600-results-shell .adventure-panel," +
      "body.diq-v600-results-shell [data-diq-adventure-panel]," +
      "body.diq-v600-results-shell [data-adventure-panel]," +
      "body.diq-v600-results-shell .diqAdventurePanel," +
      "body.diq-v600-results-shell .diq-adventure-panel," +
      "body.diq-v600-results-shell .adventure-sheet," +
      "body.diq-v600-results-shell .adventure-modal{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;transform:translateY(120vh)!important;" +
      "}" +
      "#diqV600ResultsPill{" +
        "display:none;position:fixed;" +
        "left:14px;right:auto;top:auto;" +
        "bottom:calc(92px + env(safe-area-inset-bottom,0px));transform:none;z-index:2147483646;" +
        "min-width:100px;height:40px;padding:0 15px;border-radius:999px;" +
        "align-items:center;justify-content:center;" +
        "background:linear-gradient(135deg,rgba(255,225,128,.94),rgba(63,240,188,.92));" +
        "border:1px solid rgba(255,255,255,.7);" +
        "box-shadow:0 14px 34px rgba(0,0,0,.38),0 0 0 1px rgba(255,255,255,.12) inset;" +
        "backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);" +
        "color:#07121a;font:900 13px/1 -apple-system," +
        "BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.1px;pointer-events:auto;cursor:pointer;" +
        "touch-action:manipulation;-webkit-tap-highlight-color:transparent;" +
      "}" +
      "#diqV600ResultsPill.on{" +
        "display:flex;" +
      "}" +
      "#diqV600ResultsPill:active{transform:scale(.97);}" +
      "#diqV600ResultsPill{" +
        "min-width:148px!important;height:46px!important;padding:0 16px!important;" +
        "border-radius:18px!important;" +
        "background:linear-gradient(180deg,rgba(5,22,35,.97),rgba(2,12,21,.98))!important;" +
        "border:1px solid rgba(80,231,255,.40)!important;" +
        "box-shadow:0 16px 38px rgba(0,0,0,.44),0 0 18px rgba(54,219,255,.08)!important;" +
        "color:#f8fdff!important;font-size:11.5px!important;font-weight:950!important;" +
        "letter-spacing:.35px!important;" +
      "}" +
      "#diqV600ResultsPill:before{" +
        "content:'';display:inline-block;width:9px;height:9px;flex:0 0 9px;" +
        "margin-right:9px;border-radius:3px;background:#4ce9ff;" +
        "transform:rotate(45deg);box-shadow:0 0 15px rgba(76,233,255,.76);" +
      "}" +
      ".diqV605PremiumShelf{" +
        "position:relative!important;padding-top:58px!important;" +
        "border-radius:30px 30px 0 0!important;" +
        "background:radial-gradient(circle at 18% 0%,rgba(74,231,255,.055),transparent 30%)," +
        "linear-gradient(180deg,rgba(5,17,28,.98),rgba(2,10,18,.995))!important;" +
        "border:1px solid rgba(70,224,255,.13)!important;border-bottom:0!important;" +
        "box-shadow:0 -26px 64px rgba(0,0,0,.48),0 1px 0 rgba(255,255,255,.04) inset!important;" +
        "backdrop-filter:blur(28px) saturate(1.08)!important;" +
        "-webkit-backdrop-filter:blur(28px) saturate(1.08)!important;" +
      "}" +
      ".diqV605PremiumShelfHead{" +
        "position:absolute;left:0;right:0;top:0;height:58px;" +
        "display:flex;align-items:center;justify-content:space-between;" +
        "padding:0 16px 0 18px;border-bottom:1px solid rgba(255,255,255,.07);" +
        "background:linear-gradient(180deg,rgba(7,20,32,.96),rgba(5,16,27,.86));" +
        "border-radius:30px 30px 0 0;z-index:3;" +
      "}" +
      ".diqV605PremiumShelfTitle{" +
        "color:#f7fbff;font:950 13px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.65px;" +
      "}" +
      ".diqV605PremiumShelfClose{" +
        "width:36px;height:36px;display:inline-flex;align-items:center;justify-content:center;" +
        "border-radius:14px;background:rgba(255,255,255,.065);" +
        "border:1px solid rgba(255,255,255,.12);color:#fff;" +
        "font:500 24px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "box-shadow:0 10px 24px rgba(0,0,0,.24);pointer-events:auto;" +
      "}" +
      ".diqV605FeaturedResult{" +
        "border-color:rgba(255,216,100,.42)!important;" +
        "background:radial-gradient(circle at 88% 0%,rgba(255,215,94,.08),transparent 30%)," +
        "linear-gradient(180deg,rgba(15,29,39,.98),rgba(4,15,24,.995))!important;" +
        "box-shadow:0 20px 42px rgba(0,0,0,.36),0 0 24px rgba(255,216,100,.07)!important;" +
      "}" +
      ".diqV605FeaturedResult:before{" +
        "content:'TOP PICK';display:inline-flex;align-items:center;justify-content:center;" +
        "min-height:24px;padding:0 9px;margin:0 0 8px 0;border-radius:999px;" +
        "background:rgba(255,216,100,.10);border:1px solid rgba(255,216,100,.34);" +
        "color:#ffe79a;font:950 8px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:1px;" +
      "}" +
      ".diqV605SecondaryResult{" +
        "border-color:rgba(255,255,255,.085)!important;" +
      "}" +
      "body.diq-adventure-voice-open #diqV600ResultsPill{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "body.diq-v600-independent-menu-open .topWrap{" +
        "display:block!important;visibility:visible!important;opacity:1!important;" +
        "pointer-events:auto!important;transform:none!important;" +
        "left:12px!important;right:auto!important;" +
        "width:calc(100vw - 24px)!important;max-width:420px!important;" +
        "box-sizing:border-box!important;" +
      "}" +
      "body.diq-v600-independent-menu-open #adventureHero{" +
        "display:block!important;visibility:visible!important;opacity:1!important;" +
        "pointer-events:auto!important;transform:none!important;" +
        "width:100%!important;max-width:420px!important;" +
        "box-sizing:border-box!important;" +
      "}" +
      "#diqV600ScanAgain{" +
        "display:none;position:fixed;" +
        "right:14px;top:calc(66px + env(safe-area-inset-top,0px));" +
        "left:auto;bottom:auto;z-index:2147483001;" +
        "min-width:100px;height:36px;padding:0 14px;border-radius:999px;" +
        "align-items:center;justify-content:center;gap:7px;" +
        "background:rgba(5,18,28,.9);" +
        "border:1px solid rgba(65,241,197,.5);" +
        "box-shadow:0 12px 28px rgba(0,0,0,.34);" +
        "backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);" +
        "color:#eafff8;font:850 11px/1 -apple-system," +
        "BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.35px;pointer-events:auto;cursor:pointer;" +
        "touch-action:manipulation;-webkit-tap-highlight-color:transparent;" +
      "}" +
      "#diqV600ScanAgain.on{" +
        "display:flex;animation:diqScanAgainIn .2s cubic-bezier(.2,.8,.2,1) both;" +
      "}" +
      "#diqV600ScanAgain:before{" +
        "content:'↻';font-size:16px;line-height:1;color:#42f2c4;" +
      "}" +
      "#diqV600ScanAgain:active{transform:scale(.97);}" +
      "@keyframes diqScanAgainIn{" +
        "from{opacity:0;transform:translateY(-6px) scale(.97)}" +
        "to{opacity:1;transform:translateY(0) scale(1)}" +
      "}" +
      "@keyframes diqPlacesPillIn{" +
        "from{opacity:0;transform:translateY(-8px) scale(.96)}" +
        "to{opacity:1;transform:translateY(0) scale(1)}" +
      "}" +
      "#adventureMenuRestore,#diqV528ResultsPill,#diqV527MapResultsPill," +
      "#diq-adventure-results-toggle,#diqV519Reopen,#diqV518Reopen," +
      "#diqV526PlacesReopen{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "#diqV600ResultShelf.on{" +
        "animation:diqShelfPremiumIn .28s cubic-bezier(.2,.8,.2,1) both!important;" +
      "}" +
      "#diqV600ResultShelf.on .diqV600ShelfCard{" +
        "animation:diqShelfCardIn .32s cubic-bezier(.2,.8,.2,1) both;" +
      "}" +
      "#diqV600ResultShelf.on .diqV600ShelfCard:nth-child(2){" +
        "animation-delay:.04s;" +
      "}" +
      "#diqV600ResultShelf.on .diqV600ShelfCard:nth-child(3){" +
        "animation-delay:.08s;" +
      "}" +
      "@keyframes diqShelfPremiumIn{" +
        "from{opacity:0;transform:translateY(18px) scale(.985)}" +
        "to{opacity:1;transform:translateY(0) scale(1)}" +
      "}" +
      "@keyframes diqShelfCardIn{" +
        "from{opacity:0;transform:translateY(10px)}" +
        "to{opacity:1;transform:translateY(0)}" +
      "}" +
      "#diqV600ScanStatus{" +
        "display:none;position:fixed;left:50%;" +
        "bottom:calc(96px + env(safe-area-inset-bottom,0px));" +
        "transform:translateX(-50%);z-index:2147483002;" +
        "min-width:min(86vw,360px);max-width:calc(100vw - 32px);" +
        "padding:13px 16px;border-radius:18px;" +
        "align-items:center;gap:12px;" +
        "background:rgba(3,13,22,.92);" +
        "border:1px solid rgba(67,245,199,.42);" +
        "box-shadow:0 18px 42px rgba(0,0,0,.46);" +
        "backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);" +
        "color:#effff9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "pointer-events:none;" +
      "}" +
      "#diqV600ScanStatus.on{display:flex;animation:diqScanStatusIn .2s ease both;}" +
      "#diqV600ScanStatusDot{" +
        "width:12px;height:12px;border-radius:50%;flex:0 0 auto;" +
        "background:#36f0c3;box-shadow:0 0 0 0 rgba(54,240,195,.55);" +
        "animation:diqScanPulse 1.25s ease-in-out infinite;" +
      "}" +
      "#diqV600ScanStatusText{" +
        "font-size:13px;line-height:1.3;font-weight:800;letter-spacing:.15px;" +
        "white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" +
      "}" +
      "@keyframes diqScanPulse{" +
        "0%,100%{transform:scale(.86);box-shadow:0 0 0 0 rgba(54,240,195,.42);}" +
        "50%{transform:scale(1.12);box-shadow:0 0 0 9px rgba(54,240,195,0);}" +
      "}" +
      "@keyframes diqScanStatusIn{" +
        "from{opacity:0;transform:translate(-50%,10px)}" +
        "to{opacity:1;transform:translate(-50%,0)}" +
      "}" +
      "body.diq-v600-menu #adventureMap{" +
        "pointer-events:auto;" +
      "}" +
      "body.diq-v600-scanning #adventureMap," +
      "body.diq-v600-map #adventureMap," +
      "body.diq-v600-place #adventureMap{" +
        "pointer-events:auto;" +
        "touch-action:none;" +
      "}" +
      "body.diq-v600-scanning #diqAdventureMenu," +
      "body.diq-v600-scanning #diqAdventureSearchResults," +
      "body.diq-v600-scanning #diqV500SearchPanel," +
      "body.diq-v600-scanning #diqAdventureScanTab," +
      "body.diq-v600-scanning #diqV600ResultShelf," +
      "body.diq-v600-scanning #diqV600ResultsShelf," +
      "body.diq-v600-scanning #diqAdventureDestResults," +
      "body.diq-v600-scanning #diqV519Shelf," +
      "body.diq-v600-scanning [data-diq-destination-results]," +
      "body.diq-v600-scanning .diqDestinationResults," +
      "body.diq-v600-scanning .diqSearchResults," +
      "body.diq-v600-scanning .adventureSearchResults," +
      "body.diq-v600-scanning [data-search-results]," +
      "body.diq-v600-scanning [data-autocomplete-results]," +
      "body.diq-v600-scanning [data-location-suggestions]," +
      "body.diq-v600-scanning [class*='autocomplete']," +
      "body.diq-v600-scanning [class*='destination-results']{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "body.diq-v600-browse #diqAdventureMenu," +
      "body.diq-v600-browse #diqAdventureSearchResults," +
      "body.diq-v600-browse #diqV500SearchPanel," +
      "body.diq-v600-browse #diqAdventureScanTab," +
      "body.diq-v600-browse [data-diq-destination-results]," +
      "body.diq-v600-browse .diqDestinationResults," +
      "body.diq-v600-browse .diqSearchResults," +
      "body.diq-v600-browse .adventureSearchResults," +
      "body.diq-v600-browse [data-search-results]," +
      "body.diq-v600-browse [data-autocomplete-results]," +
      "body.diq-v600-browse [data-location-suggestions]," +
      "body.diq-v600-browse [class*='autocomplete']," +
      "body.diq-v600-browse [class*='destination-results']{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "body.diq-v600-destination-editing #diqV600ScanAgain," +
      "body.diq-v600-destination-editing #diqV600ResultShelf," +
      "body.diq-v600-destination-editing #diqV600ResultsShelf," +
      "body.diq-v600-destination-editing #diqAdventureDestResults," +
      "body.diq-v600-destination-editing #diqV519Shelf," +
      "body.diq-v600-destination-editing .diqAdventureResultsShelf," +
      "body.diq-v600-destination-editing .diqV600ResultShelf," +
      "body.diq-v600-destination-editing .diqV600ResultsShelf," +
      "body.diq-v600-destination-editing [data-diq-results-shelf]{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "body.diq-v600-destination-editing #diqAdventureSearchResults," +
      "body.diq-v600-destination-editing #diqV500SearchPanel," +
      "body.diq-v600-destination-editing [data-diq-destination-results]," +
      "body.diq-v600-destination-editing [data-search-results]," +
      "body.diq-v600-destination-editing [data-autocomplete-results]," +
      "body.diq-v600-destination-editing [data-location-suggestions]{" +
        "display:block!important;visibility:visible!important;opacity:1!important;" +
        "pointer-events:auto!important;" +
      "}" +
      "body.diq-v600-place #diqV600ScanAgain," +
      "body.diqPlaceIQMode #diqV600ScanAgain," +
      "body.diq-v600-browse #diqV600ScanAgain," +
      "body.diq-v600-scanning #diqV600ScanAgain{" +
        "display:none!important;visibility:hidden!important;opacity:0!important;" +
        "pointer-events:none!important;" +
      "}" +
      "body.diq-v600-place #diqV600ResultShelf," +
      "body.diq-v600-place #diqV600ResultsShelf," +
      "body.diq-v600-place #diqAdventureDestResults," +
      "body.diq-v600-place #diqV519Shelf," +
      "body.diq-v600-place .diqAdventureResultsShelf," +
      "body.diq-v600-place .diqV600ResultShelf," +
      "body.diq-v600-place .diqV600ResultsShelf," +
      "body.diq-v600-place [data-diq-results-shelf]," +
      "body.diqPlaceIQMode #diqV600ResultShelf," +
      "body.diqPlaceIQMode #diqV600ResultsShelf," +
      "body.diqPlaceIQMode #diqAdventureDestResults," +
      "body.diqPlaceIQMode #diqV519Shelf," +
      "body.diqPlaceIQMode .diqAdventureResultsShelf," +
      "body.diqPlaceIQMode .diqV600ResultShelf," +
      "body.diqPlaceIQMode .diqV600ResultsShelf," +
      "body.diqPlaceIQMode [data-diq-results-shelf]{" +
        "display:none!important;" +
        "visibility:hidden!important;" +
        "opacity:0!important;" +
        "pointer-events:none!important;" +
      "}"
    ));

    (document.head || document.documentElement)
      .appendChild(style);
  }


  function legacyControlLooksLikeMenu(control) {
    var textValue;
    var idValue;
    var classValue;

    if (!control || control.id === ids.resultsPill) {
      return false;
    }

    textValue = String(
      control.textContent || ""
    ).replace(/\s+/g, " ").trim().toUpperCase();

    idValue = String(control.id || "").toLowerCase();
    classValue = String(control.className || "").toLowerCase();

    return !!(
      textValue === "SHOW MENU" ||
      textValue === "SHOW PLACES" ||
      textValue === "PLACES FOUND" ||
      idValue.indexOf("menurestore") > -1 ||
      idValue.indexOf("showmenu") > -1 ||
      idValue.indexOf("resultsreopen") > -1 ||
      classValue.indexOf("menurestore") > -1 ||
      classValue.indexOf("show-menu") > -1
    );
  }

  function suppressLegacyControl(control) {
    if (
      !control ||
      control.id === ids.resultsPill ||
      control.id === "adventurePanelRestore"
    ) {
      return false;
    }

    control.style.setProperty("display", "none", "important");
    control.style.setProperty("visibility", "hidden", "important");
    control.style.setProperty("opacity", "0", "important");
    control.style.setProperty("pointer-events", "none", "important");
    control.setAttribute("aria-hidden", "true");
    control.setAttribute("data-diq-retired-control", "1");

    return true;
  }

  function retireLegacyResultsControls() {
    var candidates = [];

    LEGACY_RESULTS_CONTROLS.forEach(function (controlID) {
      var control = get(controlID);

      if (control) {
        candidates.push(control);
      }
    });

    try {
      Array.prototype.forEach.call(
        document.querySelectorAll(
          "button,div,[role='button'],a"
        ),
        function (control) {
          if (
            control &&
            candidates.indexOf(control) < 0 &&
            legacyControlLooksLikeMenu(control)
          ) {
            candidates.push(control);
          }
        }
      );
    } catch (error) {}

    candidates.forEach(suppressLegacyControl);

    return candidates.length;
  }

  function scheduleLegacyControlRetirement() {
    if (legacyControlTimer) {
      window.clearTimeout(legacyControlTimer);
      legacyControlTimer = null;
    }

    /*
      Stack-safe retirement:
      run the actual suppressor now and on bounded delayed passes.
      Never call this scheduler from inside itself.
    */
    retireLegacyResultsControls();

    legacyControlTimer = window.setTimeout(function () {
      legacyControlTimer = null;
      retireLegacyResultsControls();
    }, 80);

    window.setTimeout(
      retireLegacyResultsControls,
      260
    );

    window.setTimeout(
      retireLegacyResultsControls,
      700
    );

    return true;
  }



  function clearPinHighlightTimers() {
    pinHighlightTimers.forEach(function (timerID) {
      window.clearTimeout(timerID);
    });

    pinHighlightTimers.length = 0;
  }

  function clearGoldReassertTimers() {
    goldReassertTimers.forEach(function (timerID) {
      window.clearTimeout(timerID);
    });

    goldReassertTimers.length = 0;
  }

  function selectedPlaceFromState() {
    return (
      activePlaceItem ||
      state.get("placeCard.item", null) ||
      state.get("selectedPlace", null) ||
      state.get("place.selected", null) ||
      null
    );
  }

  function applySelectedPinHighlight(item) {
    var selected = item || selectedPlaceFromState();

    if (!selected) {
      return false;
    }

    activePlaceItem = selected;

    try {
      if (
        window.DriveIQAdventurePins &&
        typeof window.DriveIQAdventurePins.highlight === "function"
      ) {
        return window.DriveIQAdventurePins.highlight(selected);
      }
    } catch (error) {}

    return false;
  }

  function persistSelectedPinHighlight(item) {
    var selected = item || selectedPlaceFromState();

    if (!selected) {
      return false;
    }

    activePlaceItem = selected;
    clearPinHighlightTimers();

    applySelectedPinHighlight(selected);

    [0, 90, 260, 620].forEach(function (delay) {
      pinHighlightTimers.push(
        window.setTimeout(function () {
          applySelectedPinHighlight(selected);
        }, delay)
      );
    });

    return true;
  }


  function reassertGoldPin(reason) {
    var selected = selectedPlaceFromState();
    var now = Date.now();

    if (!selected) {
      return false;
    }

    activePlaceItem = selected;

    /*
      Stability Authority:
      scan, shelf, and map callbacks may all request a repaint together. Keep
      one bounded repaint sequence instead of stacking a new timer batch on
      overlapping event callbacks.
    */
    applySelectedPinHighlight(selected);

    if (now - lastGoldReassertAt < 120 && goldReassertTimers.length) {
      return true;
    }

    lastGoldReassertAt = now;
    clearGoldReassertTimers();

    [80, 220, 520].forEach(function (delay) {
      goldReassertTimers.push(
        window.setTimeout(function () {
          applySelectedPinHighlight(selected);
        }, delay)
      );
    });

    try {
      events.emit("pin:gold-reasserted", {
        item: selected,
        reason: String(reason || "ui-reassert")
      });
    } catch (error) {}

    return true;
  }

  function mapCoordinate(item) {
    var lat;
    var lng;

    if (!item) {
      return null;
    }

    lat = Number(
      item.lat != null
        ? item.lat
        : item.latitude
    );

    lng = Number(
      item.lng != null
        ? item.lng
        : item.longitude
    );

    if (
      !isFinite(lat) ||
      !isFinite(lng)
    ) {
      return null;
    }

    return {
      lat: lat,
      lng: lng
    };
  }

  function focusSelectedPlace(item, sourceName) {
    var coordinate = mapCoordinate(item);
    var mapController = window.DriveIQAdventureMap;
    var mapInstance = null;
    var zoom = (
      config.map &&
      Number(config.map.placeZoom)
    ) || 15.2;

    if (!coordinate) {
      return false;
    }

    /*
      First ask the map module to focus using its normal authority. Pass the
      visual offset for builds that already support it.
    */
    try {
      if (
        mapController &&
        typeof mapController.focusPlace === "function"
      ) {
        mapController.focusPlace(item, {
          duration: 520,
          zoom: zoom,
          offset: [0, CAMERA_VERTICAL_OFFSET],
          verticalOffset: CAMERA_VERTICAL_OFFSET,
          source: sourceName || "selection"
        });
      }
    } catch (focusError) {}

    /*
      Mapbox fallback: enforce the same camera composition directly. The
      selected pin remains in the clear map area above the Place IQ card.
    */
    try {
      if (
        mapController &&
        typeof mapController.getMap === "function"
      ) {
        mapInstance = mapController.getMap();
      }

      if (
        mapInstance &&
        typeof mapInstance.easeTo === "function"
      ) {
        window.setTimeout(function () {
          try {
            mapInstance.easeTo({
              center: [coordinate.lng, coordinate.lat],
              zoom: Math.max(
                Number(mapInstance.getZoom
                  ? mapInstance.getZoom()
                  : zoom) || zoom,
                zoom
              ),
              offset: [0, CAMERA_VERTICAL_OFFSET],
              duration: 520,
              essential: true
            });
          } catch (cameraError) {}
        }, 30);

        return true;
      }
    } catch (mapError) {}

    return true;
  }

  function ensureResultsPill() {
    var pill = get(ids.resultsPill);

    if (pill) {
      return pill;
    }

    pill = document.createElement("button");
    pill.id = ids.resultsPill;
    pill.type = "button";
    pill.setAttribute("aria-live", "polite");
    pill.setAttribute("aria-hidden", "true");

    document.body.appendChild(pill);

    return pill;
  }

  function ensureScanAgainControl() {
    var control = get(ids.scanAgain);

    /*
     * Destinations is the single authority for reopening the Adventure menu.
     * Remove the obsolete New Search control completely so it cannot overlap,
     * intercept touches, or fight the Places shelf state.
     */
    if (control && control.parentNode) {
      control.parentNode.removeChild(control);
    }

    return null;
  }

  function showScanAgainControl() {
    ensureScanAgainControl();
    return false;
  }

  function hideScanAgainControl() {
    ensureScanAgainControl();
    return true;
  }

  function openScanAgain(reason) {
    var source = String(reason || "scan-again");

    browseResultsActive = false;
    resultsReturnInProgress = false;
    stopBrowseGuard();
    clearResultsReturnTimers();
    exitScanningState();

    minimizePersistentShelf(source);
    hideScanAgainControl();

    /*
     * New Search must perform the same complete visual-state recovery as the
     * persistent Destinations button. Clear stale Place IQ and results-shell
     * authority before reopening the destination editor.
     */
    try {
      if (
        window.DriveIQAdventurePlaceCard &&
        typeof window.DriveIQAdventurePlaceCard.close === "function"
      ) {
        window.DriveIQAdventurePlaceCard.close();
      }
    } catch (placeCloseError) {}

    releaseAdventureResultsPanel(source);

    if (document.body) {
      document.body.classList.remove("diq-v600-place");
      document.body.classList.remove("diqPlaceIQMode");
      document.body.classList.remove("diq-v600-map");
      document.body.classList.remove("diq-v600-results-shell");
      document.body.classList.remove("diq-v600-browse");
      document.body.classList.remove("diq-v600-scanning");
    }

    endDestinationEditing(source + "-reset", {
      restoreCommittedLabel: true
    });

    showMenu({ force: true });
    beginDestinationEditing(source);
    forceOpenAdventureMenuSurface(source);
    setDisplay(get(ids.menu), true);
    scheduleDestinationFieldSync(source);

    [0, 80, 220].forEach(function (delay) {
      window.setTimeout(function () {
        releaseAdventureResultsPanel(source + "-" + delay);

        if (document.body) {
          document.body.classList.remove("diq-v600-place");
          document.body.classList.remove("diqPlaceIQMode");
          document.body.classList.remove("diq-v600-results-shell");
          document.body.classList.remove("diq-v600-map");
          document.body.classList.add("diq-v600-menu");
          document.body.classList.add("diq-v600-destination-editing");
        }

        forceOpenAdventureMenuSurface(source + "-" + delay);
        setDisplay(get(ids.menu), true);
      }, delay);
    });

    window.setTimeout(function () {
      var fields = destinationInputFields();
      var field = fields.length ? fields[0] : null;

      if (!field) {
        return;
      }

      try {
        field.focus();
        field.setSelectionRange(
          String(field.value || "").length,
          String(field.value || "").length
        );
      } catch (error) {}
    }, 100);

    try {
      events.emit("ui:scan-again-opened", {
        reason: source,
        count: Number(state.get("results.count", 0) || 0),
        destination: destinationLabelFromState()
      });
    } catch (error) {}

    return true;
  }

  function removeModeClasses() {
    if (!document.body) {
      return;
    }

    [
      "diq-v600-menu",
      "diq-v600-scanning",
      "diq-v600-map",
      "diq-v600-place",
      "diq-v600-browse",
      "diq-v600-destination-editing",
      "diq-v600-results-shell"
    ].forEach(function (className) {
      document.body.classList.remove(className);
    });
  }

  function hideLegacyShelf() {
    var shelves = resultShelfNodes();

    shelves.forEach(function (shelf) {
      if (!shelf) {
        return;
      }

      shelf.style.display = "none";
      shelf.style.visibility = "hidden";
      shelf.style.opacity = "0";
      shelf.style.pointerEvents = "none";
      shelf.setAttribute("aria-hidden", "true");
    });

    [
      "diqV519Reopen",
      "diqV518Reopen",
      "diqV526PlacesReopen",
      "diqV525Minimize"
    ].forEach(function (id) {
      var control = get(id);

      if (control) {
        control.style.display = "none";
        control.style.pointerEvents = "none";
        control.setAttribute("aria-hidden", "true");
      }
    });

    state.merge("ui", {
      legacyShelfVisible: false
    }, {
      action: "ui:hide-legacy-shelf"
    });
  }

  function positionResultsPill() {
    var pill = get(ids.resultsPill);
    var shelf = get(ids.resultShelf) || get(ids.resultShelfPlural);
    var shelfVisible = false;
    var rect;
    var bottom;

    if (!pill) {
      return false;
    }

    try {
      shelfVisible = !!(
        shelf &&
        shelf.classList.contains("on") &&
        window.getComputedStyle(shelf).display !== "none" &&
        window.getComputedStyle(shelf).visibility !== "hidden"
      );
    } catch (error) {
      shelfVisible = false;
    }

    pill.style.setProperty("left", "14px", "important");
    pill.style.setProperty("right", "auto", "important");

    if (shelfVisible) {
      try {
        rect = shelf.getBoundingClientRect();
        bottom = Math.max(
          86,
          Math.round(window.innerHeight - rect.top + 12)
        );
        pill.style.setProperty(
          "bottom",
          "calc(" + String(bottom) + "px + env(safe-area-inset-bottom,0px))",
          "important"
        );
      } catch (error) {
        pill.style.setProperty(
          "bottom",
          "calc(52vh + env(safe-area-inset-bottom,0px))",
          "important"
        );
      }
    } else {
      pill.style.setProperty(
        "bottom",
        "calc(92px + env(safe-area-inset-bottom,0px))",
        "important"
      );
    }

    return true;
  }

  function showResultsPill(count) {
    var pill = ensureResultsPill();
    var total = Math.max(0, Number(count || 0));

    scheduleLegacyControlRetirement();

    /*
      V600.58 Places Control Authority:
      The Places pill is the permanent result-session control. It remains
      available whether the compact shelf is open or closed. Only destination
      editing, zero results, or an active full scan may hide it.
    */
    if (
      !total ||
      (
        scanningModeActive() &&
        !scanCompleted
      )
    ) {
      hideResultsPill();
      return false;
    }

    pill.textContent =
      total +
      (total === 1
        ? " PLACE NEARBY"
        : " PLACES NEARBY");

    if (!pill.classList.contains("on")) {
      pill.classList.add("on");
    }
    pill.style.pointerEvents = "auto";
    pill.style.visibility = "visible";
    pill.style.opacity = "1";
    pill.removeAttribute("inert");
    pill.setAttribute("aria-hidden", "false");
    positionResultsPill();
    window.setTimeout(positionResultsPill, 80);
    window.setTimeout(positionResultsPill, 240);
    return true;
  }

  function hideResultsPill() {
    var pill = get(ids.resultsPill);

    if (!pill) {
      return;
    }

    pill.className = "";
    pill.setAttribute("aria-hidden", "true");
  }


  function restoreResultSessionControls(reason) {
    var count = Number(state.get("results.count", 0) || 0);

    if (!count) {
      hideResultsPill();
      hideScanAgainControl();
      return false;
    }

    if (scanningModeActive() && !scanCompleted) {
      hideResultsPill();
      hideScanAgainControl();
      return false;
    }

    showResultsPill(count);

    if (
      !destinationEditingActive &&
      !browseModeActive() &&
      !placeIsOpen()
    ) {
      showScanAgainControl();
    } else {
      hideScanAgainControl();
    }

    try {
      events.emit("ui:result-session-controls-restored", {
        count: count,
        reason: String(reason || "result-session")
      });
    } catch (error) {}

    return true;
  }


  function ensureScanStatus() {
    var status = get(ids.scanStatus);
    var dot;
    var textNode;

    if (status) {
      return status;
    }

    status = document.createElement("div");
    status.id = ids.scanStatus;
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.setAttribute("aria-hidden", "true");

    dot = document.createElement("span");
    dot.id = ids.scanStatusDot;

    textNode = document.createElement("span");
    textNode.id = ids.scanStatusText;
    textNode.textContent = "Finding places worth exploring";

    status.appendChild(dot);
    status.appendChild(textNode);
    document.body.appendChild(status);

    return status;
  }

  function categoryRescanStatusMessage() {
    var label = String(categoryRescanLabel || "places")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    if (!label) {
      label = "places";
    }

    return "Finding great " + label + " nearby…";
  }

  function scanStatusMessage(payload) {
    var detail = payload && (
      payload.detail ||
      payload.message ||
      payload.stage
    );

    if (categoryRescanActive) {
      return categoryRescanStatusMessage();
    }

    if (!detail) {
      return "Finding places worth exploring";
    }

    detail = String(detail)
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return detail.charAt(0).toUpperCase() + detail.slice(1);
  }

  function showScanStatus(payload) {
    var status = ensureScanStatus();
    var textNode = get(ids.scanStatusText);

    if (scanStatusTimer) {
      window.clearTimeout(scanStatusTimer);
      scanStatusTimer = null;
    }

    if (textNode) {
      textNode.textContent = scanStatusMessage(payload);
    }

    status.className = "on";
    status.setAttribute("aria-hidden", "false");
    scanStatusVisible = true;

    return true;
  }

  function updateScanStatus(payload) {
    var textNode = get(ids.scanStatusText);

    if (!scanStatusVisible) {
      showScanStatus(payload);
      return true;
    }

    if (textNode) {
      textNode.textContent = scanStatusMessage(payload);
    }

    return true;
  }

  function hideScanStatus(delayMs) {
    var delay = Math.max(0, Number(delayMs || 0));

    if (scanStatusTimer) {
      window.clearTimeout(scanStatusTimer);
      scanStatusTimer = null;
    }

    scanStatusTimer = window.setTimeout(function () {
      var status = get(ids.scanStatus);

      scanStatusTimer = null;
      scanStatusVisible = false;

      if (status) {
        status.className = "";
        status.setAttribute("aria-hidden", "true");
      }
    }, delay);

    return true;
  }


  function scanningModeActive() {
    return !!(
      scanningActive ||
      currentMode() === "scanning" ||
      (
        document.body &&
        document.body.classList.contains("diq-v600-scanning")
      )
    );
  }

  function hideScanningConflicts() {
    if (continuationActive()) {
      keepContinuationResultsVisible(
        "hide-scanning-conflicts-blocked"
      );
      return false;
    }

    hideAllResultShelves();
    hideResultsPill();
    hideScanAgainControl();

    collapseAdventureResultsPanel("scanning-conflict");
    setDisplay(get(ids.searchPanel), false);
    setDisplay(get(ids.sourceSearchPanel), false);

    /*
      The compact V600 scan-status pill replaces the old full-width scan tab.
      This runs once when a full scan starts. No repeating timer is used.
    */
    setDisplay(get(ids.scanOverlay), false);

    lockDestinationChooser("scanning-conflict");

    return true;
  }


  function enterScanningState(payload) {
    var categorySwitch = categoryRescanActive === true;

    if (continuationActive(payload)) {
      keepContinuationResultsVisible(
        "enter-scanning-blocked-continuation"
      );
      return false;
    }

    /*
      Category-dock rescans are map-first transitions. Do not release the
      collapsed Adventure setup panel, because doing so briefly restores the
      full menu before Scan IQ begins. Initial/manual scans retain the original
      presentation path unchanged.
    */
    if (!categorySwitch) {
      releaseAdventureResultsPanel("scan-started");
    }

    scanningActive = true;
    scanCompleted = false;
    browseResultsActive = false;
    stopBrowseGuard();

    removeModeClasses();

    if (document.body) {
      document.body.classList.remove("diqPlaceIQMode");
      document.body.classList.add("diq-v600-scanning");

      if (categorySwitch) {
        document.body.classList.add("diq-v600-map");
        document.body.classList.add("diq-v600-results-shell");
        document.body.classList.add("diq-v600-category-rescan");
      }
    }

    setDisplay(get(ids.placeCard), false);
    lockDestinationChooser("scan-started");

    if (categorySwitch) {
      hideAllResultShelves();
      hideResultsPill();
      hideScanAgainControl();
      setDisplay(get(ids.scanOverlay), false);
      forceCloseAdventureMenuSurface("category-rescan-scan-started");
      showScanStatus(payload || {});
      refreshCategoryDockVisibility("category-rescan-started");
      restoreMapInteractionAuthority("category-rescan-started");
    } else {
      hideScanningConflicts();
      showScanStatus(payload || {});
    }

    try {
      if (typeof state.setUIState === "function") {
        state.setUIState("scanning");
      }
    } catch (error) {}
    return true;
  }

  function exitScanningState() {
    scanningActive = false;
    if (document.body) {
      document.body.classList.remove("diq-v600-scanning");
    }

    hideScanStatus(0);
    return true;
  }

  function adventurePanelNodes() {
    var menu = get(ids.menu);
    var map = get(ids.map);
    var nodes = [];
    var seen = [];

    function add(node) {
      if (
        !node ||
        node === document.body ||
        node === document.documentElement ||
        node === map ||
        (map && node.contains && node.contains(map)) ||
        seen.indexOf(node) >= 0
      ) {
        return;
      }

      seen.push(node);
      nodes.push(node);
    }

    add(menu);

    try {
      Array.prototype.forEach.call(
        document.querySelectorAll(
          "[data-diq-adventure-menu]," +
          "[data-diq-adventure-panel]," +
          "[data-adventure-panel]," +
          "[data-adventure-menu]," +
          ".diqAdventureMenu," +
          ".diqAdventurePanel," +
          ".diq-adventure-menu," +
          ".diq-adventure-panel," +
          ".adventure-menu-panel," +
          ".adventure-panel," +
          ".adventure-sheet," +
          ".adventure-modal"
        ),
        add
      );
    } catch (error) {}

    /*
      V603 structural fallback:
      Some builds render the setup panel under a generated wrapper without one
      of the canonical classes above. Identify only large visible overlays that
      contain the Adventure setup copy, and never select the map or an ancestor
      that owns the map.
    */
    try {
      Array.prototype.forEach.call(
        document.querySelectorAll("section,aside,div"),
        function (node) {
          var content;
          var rect;

          if (
            !node ||
            node === document.body ||
            node === document.documentElement ||
            node === map ||
            (map && node.contains && node.contains(map)) ||
            seen.indexOf(node) >= 0
          ) {
            return;
          }

          content = String(node.textContent || "")
            .replace(/\s+/g, " ")
            .trim()
            .toUpperCase();

          if (
            content.indexOf("ADVENTURE IQ") < 0 ||
            content.indexOf("WHERE ARE YOU GOING") < 0
          ) {
            return;
          }

          try {
            rect = node.getBoundingClientRect();
          } catch (error) {
            rect = null;
          }

          if (
            rect &&
            rect.width >= Math.min(280, window.innerWidth * 0.7) &&
            rect.height >= Math.min(260, window.innerHeight * 0.3)
          ) {
            add(node);
          }
        }
      );
    } catch (error) {}

    /*
      Catch a visual wrapper around #diqAdventureMenu. Stop before any ancestor
      that owns the map so the map can never be hidden.
    */
    if (menu) {
      var parent = menu.parentElement;
      var depth = 0;

      while (parent && depth < 4) {
        if (
          parent === document.body ||
          parent === document.documentElement ||
          (map && parent.contains && parent.contains(map))
        ) {
          break;
        }

        add(parent);
        parent = parent.parentElement;
        depth += 1;
      }
    }

    return nodes;
  }

  function collapseAdventureResultsPanel(reason) {
    var source = String(reason || "results-panel");
    var count = Number(state.get("results.count", 0) || 0);

    if (
      independentMenuOpen ||
      count <= 0 ||
      destinationEditingActive ||
      scanningModeActive() ||
      browseModeActive() ||
      placeIsOpen()
    ) {
      return false;
    }

    if (document.body) {
      document.body.classList.add("diq-v600-map");
      document.body.classList.add("diq-v600-results-shell");
      document.body.classList.remove("diq-v600-menu");
      document.body.classList.remove("diq-v600-destination-editing");
    }

    adventurePanelNodes().forEach(function (node) {
      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("opacity", "0", "important");
      node.style.setProperty("pointer-events", "none", "important");
      node.style.setProperty("transform", "translateY(120vh)", "important");
      node.setAttribute("aria-hidden", "true");
      node.setAttribute("data-diq-results-panel-collapsed", source);
    });

    return true;
  }

  function releaseAdventureResultsPanel(reason) {
    if (resultsPanelGuardTimer) {
      window.clearInterval(resultsPanelGuardTimer);
      resultsPanelGuardTimer = null;
    }

    resultsPanelGuardUntil = 0;

    adventurePanelNodes().forEach(function (node) {
      [
        "display",
        "visibility",
        "opacity",
        "pointer-events",
        "transform"
      ].forEach(function (property) {
        node.style.removeProperty(property);
      });

      node.removeAttribute("data-diq-results-panel-collapsed");
      node.setAttribute("aria-hidden", "false");
    });

    if (document.body) {
      document.body.classList.remove("diq-v600-results-shell");
    }

    return true;
  }

  function holdResultsPanelAuthority(reason) {
    var source = String(reason || "results-panel-authority");

    if (resultsPanelGuardTimer) {
      window.clearInterval(resultsPanelGuardTimer);
      resultsPanelGuardTimer = null;
    }

    resultsPanelGuardUntil = Date.now() + 15000;
    collapseAdventureResultsPanel(source);

    resultsPanelGuardTimer = window.setInterval(function () {
      if (
        Date.now() >= resultsPanelGuardUntil ||
        independentMenuOpen ||
        destinationEditingActive ||
        scanningModeActive() ||
        browseModeActive() ||
        placeIsOpen()
      ) {
        window.clearInterval(resultsPanelGuardTimer);
        resultsPanelGuardTimer = null;
        return;
      }

      collapseAdventureResultsPanel(source + "-guard");
      restoreMapInteractionAuthority(source + "-guard");
      showResultsPill(
        Number(state.get("results.count", 0) || 0)
      );
      showScanAgainControl();
    }, 120);

    return true;
  }


  function clearTouchAuthorityTimers() {
    touchAuthorityTimers.forEach(function (timerID) {
      window.clearTimeout(timerID);
    });

    touchAuthorityTimers.length = 0;
    return true;
  }

  function protectedTouchSurface(node) {
    var map = get(ids.map);
    var pill = get(ids.resultsPill);
    var scanAgain = get(ids.scanAgain);
    var placeCard = get(ids.placeCard);

    if (!node) {
      return true;
    }

    if (
      node === document.body ||
      node === document.documentElement ||
      node === map ||
      node === pill ||
      node === scanAgain ||
      node === placeCard
    ) {
      return true;
    }

    try {
      if (
        (map && map.contains(node)) ||
        (map && node.contains && node.contains(map)) ||
        (pill && pill.contains(node)) ||
        (scanAgain && scanAgain.contains(node)) ||
        (placeCard && placeCard.contains(node)) ||
        node.closest("#" + ids.resultShelf) ||
        node.closest("#" + ids.resultShelfPlural) ||
        node.closest(".mapboxgl-control-container") ||
        node.closest(".mapboxgl-marker") ||
        node.closest("[data-diq-adventure-pin]") ||
        node.closest(".diqAdventurePin") ||
        node.closest(".diq-v600-pin") ||
        node.closest("[data-diq-place-card]") ||
        node.closest(".diqPlaceIQCard")
      ) {
        return true;
      }
    } catch (error) {}

    return false;
  }

  function looksLikeViewportTouchBlocker(node) {
    var rect;
    var style;
    var textValue;
    var classValue;
    var idValue;
    var areaRatio;

    if (!node || protectedTouchSurface(node)) {
      return false;
    }

    try {
      rect = node.getBoundingClientRect();
      style = window.getComputedStyle(node);
    } catch (error) {
      return false;
    }

    if (
      !rect ||
      rect.width < window.innerWidth * 0.72 ||
      rect.height < window.innerHeight * 0.55
    ) {
      return false;
    }

    areaRatio =
      (rect.width * rect.height) /
      Math.max(1, window.innerWidth * window.innerHeight);

    if (areaRatio < 0.48) {
      return false;
    }

    if (
      style.pointerEvents === "none" ||
      style.display === "none" ||
      style.visibility === "hidden"
    ) {
      return false;
    }

    textValue = String(node.textContent || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    classValue = String(node.className || "").toLowerCase();
    idValue = String(node.id || "").toLowerCase();

    return !!(
      node.getAttribute("aria-hidden") === "true" ||
      node.hasAttribute("inert") ||
      Number(style.opacity || 1) <= 0.08 ||
      /adventure|destination|search|chooser|overlay|sheet|panel|modal|scan/.test(
        idValue + " " + classValue
      ) ||
      (
        textValue.length < 420 &&
        /where are you going|adventure iq|choose a destination|scan again|search destination/.test(
          textValue
        )
      )
    );
  }

  function releaseViewportTouchBlockers(reason) {
    var source = String(reason || "touch-authority");
    var samplePoints = [
      [window.innerWidth * 0.5, window.innerHeight * 0.18],
      [window.innerWidth * 0.25, window.innerHeight * 0.42],
      [window.innerWidth * 0.5, window.innerHeight * 0.5],
      [window.innerWidth * 0.75, window.innerHeight * 0.42],
      [window.innerWidth * 0.5, window.innerHeight * 0.72],
      [window.innerWidth - 72, 150]
    ];
    var candidates = [];
    var seen = [];

    samplePoints.forEach(function (point) {
      var stack = [];

      try {
        stack = document.elementsFromPoint(
          Math.max(1, Math.min(window.innerWidth - 1, point[0])),
          Math.max(1, Math.min(window.innerHeight - 1, point[1]))
        ) || [];
      } catch (error) {}

      Array.prototype.forEach.call(stack, function (node) {
        if (
          node &&
          seen.indexOf(node) < 0 &&
          looksLikeViewportTouchBlocker(node)
        ) {
          seen.push(node);
          candidates.push(node);
        }
      });
    });

    candidates.forEach(function (node) {
      node.style.setProperty("pointer-events", "none", "important");
      node.setAttribute("aria-hidden", "true");
      node.setAttribute("data-diq-touch-blocker-released", source);
    });

    return candidates.length;
  }

  function holdMapTouchAuthority(reason) {
    var source = String(reason || "map-touch-authority");

    clearTouchAuthorityTimers();
    releaseViewportTouchBlockers(source);

    [0, 80, 220, 520, 1100, 2200].forEach(function (delay) {
      touchAuthorityTimers.push(
        window.setTimeout(function () {
          releaseViewportTouchBlockers(source + "-" + delay);
          var pill = get(ids.resultsPill);
          var scanAgain = get(ids.scanAgain);
          var map = get(ids.map);

          ensurePersistentNavigationControls();

          if (pill) {
            pill.style.setProperty("pointer-events", "auto", "important");
            pill.style.setProperty("z-index", "2147483646", "important");
          }

          if (scanAgain) {
            scanAgain.style.setProperty("pointer-events", "auto", "important");
            scanAgain.style.setProperty("z-index", "2147483646", "important");
          }

          if (map) {
            map.style.setProperty("pointer-events", "auto", "important");
          }

          try {
            Array.prototype.forEach.call(
              document.querySelectorAll(
                ".mapboxgl-marker,[data-diq-adventure-pin]," +
                ".diqAdventurePin,.diq-v600-pin"
              ),
              function (node) {
                node.style.setProperty(
                  "pointer-events",
                  "auto",
                  "important"
                );
              }
            );
          } catch (error) {}
        }, delay)
      );
    });

    return true;
  }


  function restoreMapInteractionAuthority(reason) {
    var source = String(reason || "result-shell");
    var map = get(ids.map);
    var menu = get(ids.menu);
    var blockers = [
      get(ids.searchPanel),
      get(ids.sourceSearchPanel),
      get(ids.scanOverlay)
    ];

    /*
      The full Adventure menu may be visually minimized by another module while
      its outer container still covers the viewport. In map-results mode it must
      not receive touches. Compact controls are restored separately below.
    */
    if (menu) {
      collapseAdventureResultsPanel(source);
    }

    blockers.forEach(function (node) {
      if (!node) {
        return;
      }

      node.style.pointerEvents = "none";
      node.setAttribute("aria-hidden", "true");
      node.removeAttribute("inert");
    });

    if (map) {
      map.style.pointerEvents = "auto";
      map.style.touchAction = "none";
      map.style.visibility = "visible";
      map.style.opacity = "1";
      map.removeAttribute("inert");
      map.setAttribute("aria-hidden", "false");
    }

    try {
      Array.prototype.forEach.call(
        document.querySelectorAll(
          ".mapboxgl-canvas-container,.mapboxgl-canvas," +
          ".mapboxgl-marker,[data-diq-adventure-pin]," +
          ".diqAdventurePin,.diq-v600-pin"
        ),
        function (node) {
          node.style.pointerEvents = "auto";
          node.removeAttribute("inert");
          node.setAttribute("aria-hidden", "false");
        }
      );
    } catch (error) {}

    /*
      Ask the controls module for its compact map shell. Never reopen the large
      destination menu merely to restore bottom controls.
    */
    try {
      events.emit("ui:adventure-controls-restore-requested", {
        reason: source,
        preserveDestination: true,
        preserveResults: true,
        compact: true
      });
    } catch (error) {}

    try {
      if (
        window.DriveIQAdventureControls &&
        typeof window.DriveIQAdventureControls.restoreCompact === "function"
      ) {
        window.DriveIQAdventureControls.restoreCompact(source);
      } else if (
        window.DriveIQAdventureControls &&
        typeof window.DriveIQAdventureControls.showMapControls === "function"
      ) {
        window.DriveIQAdventureControls.showMapControls(source);
      }
    } catch (controlsError) {}

    holdMapTouchAuthority(source);
    return true;
  }


  function showMapResultsShell(reason) {
    var count = Number(state.get("results.count", 0) || 0);

    browseResultsActive = false;
    resultsReturnInProgress = false;
    destinationEditingActive = false;

    stopBrowseGuard();
    clearResultsReturnTimers();
    exitScanningState();
    removeModeClasses();

    if (document.body) {
      document.body.classList.remove("diqPlaceIQMode");
      document.body.classList.add("diq-v600-map");
      document.body.classList.add("diq-v600-results-shell");
    }

    setDisplay(get(ids.menu), false);
    setDisplay(get(ids.searchPanel), false);
    setDisplay(get(ids.sourceSearchPanel), false);
    setDisplay(get(ids.scanOverlay), false);
    setDisplay(get(ids.placeCard), false);
    restoreMapInteractionAuthority(
      reason || "map-results-shell"
    );

    hideAllResultShelves();
    releaseResultShelfOverrides();

    if (count > 0) {
      showResultsPill(count);
      showScanAgainControl();
    } else {
      hideResultsPill();
      hideScanAgainControl();
    }

    try {
      if (typeof state.setUIState === "function") {
        state.setUIState("map");
      }
    } catch (error) {}

    scheduleDestinationFieldSync(
      reason || "map-results-shell"
    );
    scheduleLegacyControlRetirement();
    holdResultsPanelAuthority(
      reason || "map-results-shell"
    );

    [0, 90, 260, 620, 1200, 2400, 4200].forEach(function (delay) {
      window.setTimeout(function () {
        var committedCount = Number(
          state.get("results.count", 0) || 0
        );

        collapseAdventureResultsPanel(
          String(reason || "map-results-shell") +
          "-collapse-" +
          String(delay)
        );
        restoreMapInteractionAuthority(
          String(reason || "map-results-shell") +
          "-interaction-" +
          String(delay)
        );

        if (committedCount > 0 && !destinationEditingActive) {
          showResultsPill(committedCount);
          showScanAgainControl();
        }
      }, delay);
    });

    try {
      events.emit("ui:map-results-shell-shown", {
        reason: String(reason || "map-results-shell"),
        count: count
      });
    } catch (error) {}

    return true;
  }


  function premiumShelfCount() {
    return Math.max(
      0,
      Number(state.get("results.count", 0) || 0)
    );
  }

  function premiumShelfTitleText() {
    var count = premiumShelfCount();

    if (count === 1) {
      return "TOP PICKS • 1 PLACE";
    }

    return "TOP PICKS • " + String(count) + " PLACES";
  }

  function closePremiumResultsShelf(reason) {
    var source = String(reason || "premium-results-close");
    var count = premiumShelfCount();

    browseResultsActive = false;
    resultsReturnInProgress = false;
    stopBrowseGuard();
    clearResultsReturnTimers();

    minimizePersistentShelf(source);

    if (document.body) {
      document.body.classList.remove("diq-v600-browse");
      document.body.classList.add("diq-v600-map");
      document.body.classList.add("diq-v600-results-shell");
    }

    if (count > 0) {
      showResultsPill(count);
      window.setTimeout(positionResultsPill, 0);
      window.setTimeout(positionResultsPill, 120);
    }

    restoreMapInteractionAuthority(source);

    try {
      events.emit("ui:premium-results-closed", {
        reason: source,
        count: count
      });
    } catch (error) {}

    return true;
  }

  function decoratePremiumResultShelf(reason) {
    var count = premiumShelfCount();
    var shelves = resultShelfNodes();

    shelves.forEach(function (shelf) {
      var head;
      var title;
      var close;
      var cards;

      if (!shelf) {
        return;
      }

      try {
        cards = shelf.querySelectorAll(
          ".diqV600ShelfCard,[data-diq-result-card]"
        );
      } catch (error) {
        cards = [];
      }

      if (!cards.length && !shelf.children.length) {
        return;
      }

      shelf.classList.add("diqV605PremiumShelf");
      shelf.setAttribute("data-diq-premium-results", "1");

      head = shelf.querySelector(".diqV605PremiumShelfHead");

      if (!head) {
        head = document.createElement("div");
        head.className = "diqV605PremiumShelfHead";

        title = document.createElement("div");
        title.className = "diqV605PremiumShelfTitle";

        close = document.createElement("button");
        close.type = "button";
        close.className = "diqV605PremiumShelfClose";
        close.textContent = "×";
        close.setAttribute("aria-label", "Close Top Picks");
        close.setAttribute("title", "Close Top Picks");

        close.addEventListener("click", function (event) {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }

          closePremiumResultsShelf("premium-top-picks-close");
        }, false);

        head.appendChild(title);
        head.appendChild(close);

        if (shelf.firstChild) {
          shelf.insertBefore(head, shelf.firstChild);
        } else {
          shelf.appendChild(head);
        }
      }

      title = head.querySelector(".diqV605PremiumShelfTitle");
      if (title) {
        title.textContent = premiumShelfTitleText();
      }

      try {
        cards = shelf.querySelectorAll(
          ".diqV600ShelfCard,[data-diq-result-card]"
        );
      } catch (error) {
        cards = [];
      }

      Array.prototype.forEach.call(cards, function (card, index) {
        if (!card) {
          return;
        }

        card.classList.toggle("diqV605FeaturedResult", index === 0);
        card.classList.toggle("diqV605SecondaryResult", index > 0);
        card.setAttribute(
          "data-diq-premium-rank",
          index === 0 ? "featured" : "standard"
        );
      });
    });

    try {
      events.emit("ui:premium-results-decorated", {
        reason: String(reason || "premium-results"),
        count: count
      });
    } catch (error) {}

    return true;
  }

  function schedulePremiumResultShelfDecoration(reason) {
    [0, 60, 180, 420].forEach(function (delay) {
      window.setTimeout(function () {
        decoratePremiumResultShelf(
          String(reason || "premium-results") + "-" + String(delay)
        );
      }, delay);
    });

    return true;
  }

  function toggleResultsShelf(reason) {
    var scan = scanController();
    var source = String(reason || "places-button");
    var wasVisible = false;
    var opened = false;
    var count = Number(state.get("results.count", 0) || 0);

    try {
      wasVisible = !!(
        scan &&
        typeof scan.isResultShelfVisible === "function" &&
        scan.isResultShelfVisible()
      );
    } catch (error) {}

    /*
     * Independent Places Authority:
     * Toggle only the bottom shelf. Do not enter browse mode, close the top
     * Destinations menu, end destination editing, or hide the Places pill.
     */
    try {
      if (
        scan &&
        typeof scan.toggleResultShelf === "function"
      ) {
        opened = scan.toggleResultShelf(source) === true;
      } else if (wasVisible) {
        minimizePersistentShelf(source + "-close");
        opened = false;
      } else {
        opened = restorePersistentShelf(source + "-open") === true;
      }
    } catch (error) {
      opened = false;
    }

    browseResultsActive = false;
    resultsReturnInProgress = false;
    stopBrowseGuard();
    clearResultsReturnTimers();

    if (count > 0) {
      showResultsPill(count);
      window.setTimeout(positionResultsPill, 0);
      window.setTimeout(positionResultsPill, 120);
      window.setTimeout(positionResultsPill, 320);
    }

    if (!opened) {
      restoreMapInteractionAuthority(source + "-closed");
    } else {
      schedulePremiumResultShelfDecoration(source + "-opened");
    }

    try {
      events.emit("ui:places-toggle", {
        open: opened,
        reason: source,
        count: count,
        independent: true
      });
    } catch (error) {}

    return opened;
  }

  function showMenu(options) {
    var supplied = options || {};
    var menu = get(ids.menu);

    if (!supplied.force && browseModeActive()) {
      enforceResultsBrowseState();
      showOnlyResultShelf();
      return false;
    }

    browseResultsActive = false;
    releaseAdventureResultsPanel("show-menu");
    unlockDestinationChooser("show-menu");
    exitScanningState();
    removeModeClasses();
    document.body.classList.add("diq-v600-menu");

    setDisplay(menu, true);

    hideResultsPill();
    hideScanAgainControl();
    minimizePersistentShelf("show-menu");
    scheduleDestinationFieldSync("show-menu");

    state.setUIState("menu");

    events.emit("ui:mode-changed", {
      mode: "menu"
    });
  }

  function showScanning(payload, options) {
    var supplied = options || {};

    if (!supplied.force && browseModeActive()) {
      enforceResultsBrowseState();
      showOnlyResultShelf();
      return false;
    }

    enterScanningState(payload || {});

    events.emit("ui:mode-changed", {
      mode: "scanning"
    });

    return true;
  }

  function showMap(options) {
    var supplied = options || {};
    var count = state.get("results.count", 0);

    /*
      Browse Results Authority:
      generic map restoration must never replace active results browsing.
    */
    if (!supplied.force && browseModeActive()) {
      enforceResultsBrowseState();
      showOnlyResultShelf();
      return false;
    }

    /*
      Place IQ Authority:
      delayed scan/photo/map callbacks may request map mode after the place
      card has already opened. Ignore those requests until Place IQ closes.
    */
    if (!supplied.force && placeIsOpen()) {
      hideAllResultShelves();
      hideResultsPill();
      return false;
    }

    exitScanningState();
    removeModeClasses();
    document.body.classList.remove("diqPlaceIQMode");
    document.body.classList.add("diq-v600-map");

    setDisplay(get(ids.menu), false);
    setDisplay(get(ids.scanOverlay), false);
    setDisplay(get(ids.placeCard), false);

    releaseResultShelfOverrides();
    hideLegacyShelf();

    if (count > 0) {
      showResultsPill(count);
    } else {
      hideResultsPill();
    }

    state.setUIState("map");
    scheduleDestinationFieldSync("show-map");
    scheduleLegacyControlRetirement();

    events.emit("ui:mode-changed", {
      mode: "map"
    });

    return true;
  }

  function showPlace() {
    lockDestinationChooser("place-open");
    exitScanningState();
    removeModeClasses();
    document.body.classList.add("diq-v600-place");
    document.body.classList.add("diqPlaceIQMode");

    setDisplay(get(ids.menu), false);
    setDisplay(get(ids.scanOverlay), false);
    setDisplay(get(ids.placeCard), true);

    minimizePersistentShelf("place-open");
    hideScanAgainControl();

    if (state.get("results.count", 0) > 0) {
      showResultsPill(
        state.get("results.count", 0)
      );
    } else {
      hideResultsPill();
    }

    state.setUIState("place");
    persistSelectedPinHighlight(
      selectedPlaceFromState()
    );
    reassertGoldPin("show-place");

    /*
      Scan and photo callbacks can repaint the shelf on the same turn.
      Reassert exclusive Place IQ mode after those callbacks settle.
    */
    window.setTimeout(function () {
      minimizePersistentShelf("place-open-settled-0");
    }, 0);

    window.setTimeout(function () {
      minimizePersistentShelf("place-open-settled-120");
    }, 120);

    window.setTimeout(function () {
      minimizePersistentShelf("place-open-settled-420");
    }, 420);
    scheduleLegacyControlRetirement();

    events.emit("ui:mode-changed", {
      mode: "place"
    });

    return true;
  }


  function placeKey(item) {
    if (!item) {
      return "";
    }

    return String(
      item.placeId ||
      item.place_id ||
      item.googlePlaceId ||
      item.google_place_id ||
      item.id ||
      item.label ||
      item.name ||
      ""
    ) + "|" +
    String(
      item.lat != null
        ? item.lat
        : item.latitude != null
          ? item.latitude
          : ""
    ) + "|" +
    String(
      item.lng != null
        ? item.lng
        : item.longitude != null
          ? item.longitude
          : ""
    );
  }

  function selectionItem(eventOrPayload) {
    var payload = eventOrPayload &&
      eventOrPayload.payload
      ? eventOrPayload.payload
      : eventOrPayload || {};

    return (
      payload.item ||
      payload.place ||
      payload.result ||
      (
        payload.payload &&
        (
          payload.payload.item ||
          payload.payload.place ||
          payload.payload.result
        )
      ) ||
      (
        payload.name ||
        payload.label ||
        payload.placeId ||
        payload.place_id
          ? payload
          : null
      )
    );
  }

  function clearPlaceOpenWatchdog() {
    if (placeOpenWatchdogTimer) {
      window.clearTimeout(placeOpenWatchdogTimer);
      placeOpenWatchdogTimer = null;
    }

    return true;
  }

  function placeCardVisiblyOpen() {
    var placeCard = get(ids.placeCard);

    if (!placeCard) {
      return false;
    }

    return !!(
      placeCard.classList.contains("diqPlaceIQOpen") &&
      placeCard.style.display !== "none" &&
      placeCard.getAttribute("aria-hidden") !== "true" &&
      (
        placeCard.querySelector(".diqV600PlaceCardInner") ||
        placeCard.querySelector(".diqV600PlaceTitle")
      )
    );
  }

  function restoreAfterFailedPlaceOpen(item, sourceName, reason) {
    clearPlaceOpenWatchdog();
    openingPlaceKey = "";

    if (openingPlaceTimer) {
      window.clearTimeout(openingPlaceTimer);
      openingPlaceTimer = null;
    }

    try {
      if (document.body) {
        document.body.classList.remove("diqPlaceIQMode");
        document.body.classList.remove("diq-v600-place");
      }
    } catch (bodyError) {}

    try {
      setDisplay(get(ids.placeCard), false);
    } catch (cardError) {}

    browseResultsActive = true;
    resultsReturnInProgress = false;

    try {
      enforceResultsBrowseState();
      releaseResultShelfOverrides();
      restorePersistentShelf("place-open-failed");
    } catch (shelfError) {}

    if (item) {
      activePlaceItem = item;
      applySelectedPinHighlight(item);
    }

    hideResultsPill();

    try {
      events.emit("ui:place-open-recovered", {
        item: item || null,
        source: String(sourceName || "selection"),
        reason: String(reason || "place-card-not-visible")
      });
    } catch (eventError) {}

    try {
      showToast("That place could not open. Your results are still here.");
    } catch (toastError) {}

    return false;
  }

  function startPlaceOpenWatchdog(item, sourceName) {
    clearPlaceOpenWatchdog();

    placeOpenWatchdogTimer = window.setTimeout(function () {
      placeOpenWatchdogTimer = null;

      if (placeCardVisiblyOpen()) {
        return;
      }

      restoreAfterFailedPlaceOpen(
        item,
        sourceName,
        "place-open-watchdog"
      );
    }, PLACE_OPEN_WATCHDOG_MS);

    return true;
  }

  function callPlaceController(item, sourceName) {
    var controllers = [
      window.DriveIQAdventurePlaceIQ,
      window.DriveIQAdventurePlaceCard,
      window.DriveIQAdventurePlace,
      window.DriveIQPlaceIQ
    ];
    var methods = [
      "open",
      "openPlace",
      "show",
      "showPlace"
    ];
    var controllerIndex;
    var methodIndex;
    var controller;
    var method;

    for (
      controllerIndex = 0;
      controllerIndex < controllers.length;
      controllerIndex += 1
    ) {
      controller = controllers[controllerIndex];

      if (!controller) {
        continue;
      }

      for (
        methodIndex = 0;
        methodIndex < methods.length;
        methodIndex += 1
      ) {
        method = controller[methods[methodIndex]];

        if (typeof method !== "function") {
          continue;
        }

        try {
          var result = method.call(
            controller,
            item,
            sourceName || "selection"
          );

          if (result === false) {
            continue;
          }

          return true;
        } catch (error) {
          log(
            "Place controller method failed:",
            methods[methodIndex],
            error
          );
        }
      }
    }

    return false;
  }

  function emitPlaceOpenRequest(item, sourceName) {
    var detail = {
      item: item,
      place: item,
      result: item,
      source: String(sourceName || "selection"),
      key: placeKey(item)
    };

    /*
      Compatibility requests only. The existing Place IQ controller should
      respond by rendering the card and emitting place:opened.
    */
    events.emit("place:open-requested", detail);
    events.emit("place:requested", detail);
    events.emit("result:open-requested", detail);
    events.emit("selection:place-requested", detail);

    try {
      window.dispatchEvent(
        new CustomEvent(
          "driveiq:adventure-place-open-requested",
          {
            detail: detail
          }
        )
      );
    } catch (error) {}
  }

  function openSelectedPlace(item, sourceName) {
    var key;
    var controllerOpened;

    if (!item) {
      return false;
    }

    key = placeKey(item);
    resultsReturnInProgress = false;
    browseResultsActive = false;
    stopBrowseGuard();
    clearResultsReturnTimers();

    /*
      Transactional Selection Authority:
      keep the current map and result shelf visible until the Place IQ card
      has actually rendered. The Place Card controller owns the moment the
      shelf is hidden.
    */
    setDisplay(get(ids.menu), false);
    setDisplay(get(ids.scanOverlay), false);

    if (
      key &&
      openingPlaceKey === key
    ) {
      return true;
    }

    openingPlaceKey = key;

    if (openingPlaceTimer) {
      window.clearTimeout(openingPlaceTimer);
    }

    openingPlaceTimer = window.setTimeout(function () {
      openingPlaceTimer = null;
      openingPlaceKey = "";
    }, 900);

    /*
      Identity Authority:
      highlight the exact committed pin before the card opens. This path does
      not emit pin:selected, so it cannot recurse back into Place IQ.
    */
    activePlaceItem = item;
    persistSelectedPinHighlight(item);

    focusSelectedPlace(
      item,
      sourceName || "selection"
    );

    /*
      Preserve the exact immutable result object in shared state before
      asking Place IQ to render it.
    */
    try {
      state.selectPlace(
        item,
        sourceName || "selection"
      );
    } catch (error) {}

    controllerOpened = callPlaceController(
      item,
      sourceName || "selection"
    );

    if (!controllerOpened) {
      emitPlaceOpenRequest(
        item,
        sourceName || "selection"
      );
    }

    startPlaceOpenWatchdog(
      item,
      sourceName || "selection"
    );

    return true;
  }

  function renderMode(mode, payload) {
    var normalized = String(mode || "menu");

    if (normalized === "scanning") {
      showScanning(payload);
      return;
    }

    if (normalized === "map") {
      showMap();
      return;
    }

    if (
      normalized === "browse_results" ||
      browseModeActive()
    ) {
      enforceResultsBrowseState();
      showOnlyResultShelf();
      return;
    }

    if (normalized === "place") {
      showPlace();
      return;
    }

    showMenu();
  }

  function updateScanProgress(payload) {
    var scanSubtitle = get(ids.scanSubtitle);
    var detail = payload && (
      payload.detail ||
      payload.stage
    );

    updateScanStatus(payload || {});

    if (!scanSubtitle || !detail) {
      return;
    }

    scanSubtitle.textContent = String(detail);
  }

  function showToast(message, durationMs) {
    var toast = get(ids.toast);
    var duration = Number(durationMs || 1900);

    if (!toast) {
      log("Toast:", message);
      return false;
    }

    toast.textContent = String(
      message || "Adventure ready"
    );

    toast.className = "adventureToast on";

    state.merge("ui", {
      toastMessage: String(message || "")
    }, {
      action: "ui:toast"
    });

    if (toastTimer) {
      window.clearTimeout(toastTimer);
    }

    toastTimer = window.setTimeout(function () {
      toastTimer = null;
      toast.className = "adventureToast";

      state.merge("ui", {
        toastMessage: ""
      }, {
        action: "ui:toast-clear"
      });
    }, duration);

    return true;
  }


  function clearResultsReturnTimers() {
    resultsReturnTimers.forEach(function (timerID) {
      window.clearTimeout(timerID);
    });

    resultsReturnTimers.length = 0;
  }



  function stopDestinationChooserLock() {
    if (destinationChooserLockTimer) {
      window.clearInterval(destinationChooserLockTimer);
      destinationChooserLockTimer = null;
    }

    return true;
  }

  function closeDestinationSearchController() {
    try {
      if (
        window.DRIVE_IQ_ADVENTURE_SEARCH_V500 &&
        typeof window.DRIVE_IQ_ADVENTURE_SEARCH_V500.close === "function"
      ) {
        window.DRIVE_IQ_ADVENTURE_SEARCH_V500.close();
      }
    } catch (error) {}

    return true;
  }

  function clearDestinationSearchInput() {
    var fields = destinationInputFields();

    fields.forEach(function (field) {
      try {
        field.blur();
        field.setAttribute("aria-expanded", "false");
      } catch (fieldError) {}
    });

    /*
      Never clear the committed destination label merely because the chooser
      closes. The field is a view of committed destination state, not a
      disposable autocomplete buffer.
    */
    scheduleDestinationFieldSync("chooser-closed");

    return fields.length;
  }

  function lockDestinationChooser(reason) {
    if (destinationEditingActive) {
      return false;
    }

    destinationChooserLocked = true;

    closeDestinationSearchController();
    clearDestinationSearchInput();
    suppressDestinationChooserOverlays();

    if (destinationChooserLockTimer) {
      return true;
    }

    destinationChooserLockTimer = window.setInterval(function () {
      if (!destinationChooserLocked) {
        stopDestinationChooserLock();
        return;
      }

      if (!destinationEditingActive) {
        closeDestinationSearchController();
        suppressDestinationChooserOverlays();
        scheduleDestinationFieldSync("destination-lock-heartbeat");
      }
    }, 180);

    try {
      events.emit("ui:destination-chooser-locked", {
        reason: String(reason || "destination-locked")
      });
    } catch (error) {}

    return true;
  }

  function unlockDestinationChooser(reason) {
    destinationChooserLocked = false;
    stopDestinationChooserLock();

    try {
      events.emit("ui:destination-chooser-unlocked", {
        reason: String(reason || "destination-edit")
      });
    } catch (error) {}

    return true;
  }


  function chooserTextSignature(node) {
    var textValue;

    if (!node) {
      return "";
    }

    textValue = String(
      node.textContent || ""
    ).replace(/\s+/g, " ").trim().toUpperCase();

    return textValue;
  }

  function looksLikeDestinationChooser(node) {
    var textValue;
    var rect;
    var cityCount;
    var postcodeCount;

    if (
      !node ||
      node.id === ids.resultShelf ||
      node.id === ids.resultShelfPlural ||
      node.id === ids.placeCard ||
      node.id === ids.resultsPill ||
      (
        node.closest &&
        (
          node.closest("#" + ids.resultShelf) ||
          node.closest("#" + ids.resultShelfPlural) ||
          node.closest("#" + ids.placeCard)
        )
      )
    ) {
      return false;
    }

    textValue = chooserTextSignature(node);

    if (!textValue) {
      return false;
    }

    cityCount = (textValue.match(/\bCITY\b/g) || []).length;
    postcodeCount = (textValue.match(/\bPOSTCODE\b/g) || []).length;

    if (
      cityCount >= 2 ||
      postcodeCount >= 2 ||
      (
        cityCount >= 1 &&
        /UNITED STATES/.test(textValue) &&
        textValue.length > 80
      )
    ) {
      try {
        rect = node.getBoundingClientRect();
      } catch (error) {
        rect = null;
      }

      return !!(
        !rect ||
        (
          rect.width > 240 &&
          rect.height > 120 &&
          rect.top < window.innerHeight * 0.62
        )
      );
    }

    return false;
  }

  function suppressDestinationChooserOverlays() {
    var candidates = [];

    if (destinationEditingActive) {
      return 0;
    }

    try {
      Array.prototype.forEach.call(
        document.querySelectorAll(
          "section,aside,div,ul,[role='listbox'],[role='dialog']"
        ),
        function (node) {
          if (
            node &&
            looksLikeDestinationChooser(node) &&
            candidates.indexOf(node) < 0
          ) {
            candidates.push(node);
          }
        }
      );
    } catch (error) {}

    candidates.forEach(function (panel) {
      panel.classList.remove("on");
      panel.classList.remove("ready");
      panel.classList.remove("open");
      panel.classList.remove("active");
      panel.style.setProperty("display", "none", "important");
      panel.style.setProperty("visibility", "hidden", "important");
      panel.style.setProperty("opacity", "0", "important");
      panel.style.setProperty("pointer-events", "none", "important");
      panel.setAttribute("aria-hidden", "true");
      panel.setAttribute("inert", "");
      panel.setAttribute("data-diq-browse-suppressed", "1");
      panel.setAttribute("data-diq-destination-locked", "1");
    });

    return candidates.length;
  }

  function stopBrowseGuard() {
    if (browseGuardTimer) {
      window.clearInterval(browseGuardTimer);
      browseGuardTimer = null;
    }

    return true;
  }

  function runBrowseGuardPass() {
    if (destinationEditingActive) {
      stopBrowseGuard();
      hideAllResultShelves();
      if (Number(state.get("results.count", 0) || 0) > 0) {
        showResultsPill(state.get("results.count", 0));
      }
      return false;
    }

    if (!browseModeActive()) {
      stopBrowseGuard();
      return false;
    }

    hideNonResultPanels();
    suppressDestinationChooserOverlays();

    /*
      The heartbeat must stay synchronous and cheap. Delayed retirement and
      gold-pin repaint batches are reserved for real state transitions.
    */
    retireLegacyResultsControls();

    if (activePlaceItem) {
      applySelectedPinHighlight(activePlaceItem);
    }

    return true;
  }

  function startBrowseGuard() {
    stopBrowseGuard();
    runBrowseGuardPass();

    browseGuardTimer = window.setInterval(function () {
      runBrowseGuardPass();
    }, BROWSE_GUARD_INTERVAL_MS);

    return true;
  }

  function hideNonResultPanels() {
    if (destinationEditingActive) {
      return false;
    }

    var panels = [
      get(ids.menu),
      get(ids.searchPanel),
      get(ids.sourceSearchPanel),
      get(ids.scanOverlay)
    ];
    var selectors = [
      "[data-diq-destination-results]",
      ".diqDestinationResults",
      ".diqSearchResults",
      ".adventureSearchResults",
      "[data-destination-suggestions]",
      "[data-search-suggestions]",
      "[data-search-results]",
      "[data-autocomplete-results]",
      "[data-location-suggestions]",
      "[class*='autocomplete']",
      "[class*='destination-results']"
    ];

    selectors.forEach(function (selector) {
      var matches;

      try {
        matches = document.querySelectorAll(selector);
      } catch (error) {
        matches = [];
      }

      Array.prototype.forEach.call(matches, function (panel) {
        if (panel && panels.indexOf(panel) < 0) {
          panels.push(panel);
        }
      });
    });

    panels.forEach(function (panel) {
      if (!panel) {
        return;
      }

      panel.classList.remove("on");
      panel.classList.remove("ready");
      panel.classList.remove("open");
      panel.classList.remove("active");
      panel.style.setProperty("display", "none", "important");
      panel.style.setProperty("visibility", "hidden", "important");
      panel.style.setProperty("opacity", "0", "important");
      panel.style.setProperty("pointer-events", "none", "important");
      panel.setAttribute("aria-hidden", "true");
    });

    /*
      Destination chooser state is separate from the result shelf. Hide its
      source-level panel without touching the V600 results shelf.
    */
    try {
      if (
        window.DRIVE_IQ_ADVENTURE_SEARCH_V500 &&
        typeof window.DRIVE_IQ_ADVENTURE_SEARCH_V500.close === "function"
      ) {
        window.DRIVE_IQ_ADVENTURE_SEARCH_V500.close();
      }
    } catch (error) {}

    try {
      if (typeof window.stopScanTab === "function") {
        window.stopScanTab();
      }
    } catch (error2) {}

    suppressDestinationChooserOverlays();

    return true;
  }

  function enforceResultsBrowseState() {
    if (destinationEditingActive) {
      minimizePersistentShelf("destination-editing-active");
      if (Number(state.get("results.count", 0) || 0) > 0) {
        showResultsPill(state.get("results.count", 0));
      }
      return false;
    }

    browseResultsActive = true;
    hideNonResultPanels();

    if (document.body) {
      removeModeClasses();
      document.body.classList.remove("diqPlaceIQMode");
      document.body.classList.add("diq-v600-browse");
    }

    setDisplay(get(ids.placeCard), false);

    try {
      if (typeof state.setUIState === "function") {
        state.setUIState("browse_results");
      }
    } catch (stateError) {}

    scheduleLegacyControlRetirement();
    suppressDestinationChooserOverlays();

    if (!browseGuardTimer) {
      startBrowseGuard();
    }

    return true;
  }

  function showOnlyResultShelf() {
    var shown = false;
    var populatedShelf = null;

    if (
      scanningModeActive() &&
      !scanCompleted
    ) {
      hideAllResultShelves();
      return false;
    }

    enforceResultsBrowseState();
    releaseResultShelfOverrides();

    shown = restorePersistentShelf("browse-results");

    /*
     * V604.6 Results Shelf Paint Authority:
     * Some scan builds acknowledge restoreResultShelf() without repainting the
     * already-populated shelf. Find the existing shelf that still owns result
     * cards and make that exact node visible. This never creates new results,
     * starts a scan, clears pins, or reopens the full destination menu.
     */
    resultShelfNodes().some(function (shelf) {
      var hasResults;

      if (!shelf) {
        return false;
      }

      hasResults = !!(
        shelf.children.length ||
        shelf.querySelector(
          ".diqV600ShelfCard," +
          "[data-diq-result-card]," +
          "[data-place-id]," +
          "button,a"
        )
      );

      if (hasResults) {
        populatedShelf = shelf;
        return true;
      }

      return false;
    });

    if (populatedShelf) {
      populatedShelf.style.setProperty("display", "block", "important");
      populatedShelf.style.setProperty("visibility", "visible", "important");
      populatedShelf.style.setProperty("opacity", "1", "important");
      populatedShelf.style.setProperty("pointer-events", "auto", "important");
      populatedShelf.style.removeProperty("transform");
      populatedShelf.classList.add("on");
      populatedShelf.removeAttribute("inert");
      populatedShelf.setAttribute("aria-hidden", "false");
      populatedShelf.setAttribute("data-diq-force-visible", "browse-results");
      shown = true;
    }

    if (shown) {
      hideResultsPill();
      hideScanAgainControl();
      schedulePremiumResultShelfDecoration("browse-results-restored");

      if (activePlaceItem) {
        applySelectedPinHighlight(activePlaceItem);
        reassertGoldPin("browse-results-restored");
      }
    } else {
      showResultsPill(
        state.get("results.count", 0)
      );
    }

    return shown;
  }

  function scheduleResultsBrowseEnforcement() {
    clearResultsReturnTimers();

    [0, 80, 240, 600].forEach(function (delay) {
      resultsReturnTimers.push(
        window.setTimeout(function () {
          enforceResultsBrowseState();
          showOnlyResultShelf();

          if (activePlaceItem) {
            applySelectedPinHighlight(activePlaceItem);
          }
        }, delay)
      );
    });

    return true;
  }

  function reopenResultsShelf() {
    return toggleResultsShelf("places-button");
  }



  /*
    V600.57 Hard Result Settlement Authority:
    A valid committed result set is stronger evidence than any stale scanning
    flag, delayed progress callback, or missed scan:completed listener.
  */
  function releaseStaleScanningSurfaces(reason) {
    var source = String(reason || "usable-results-settled");
    var nodes = [];

    scanningActive = false;
    scanCompleted = true;
    calmContinuationActive = false;
    calmContinuationToken = "";

    exitScanningState();
    hideScanStatus(0);
    setDisplay(get(ids.scanOverlay), false);

    if (document.body) {
      document.body.classList.remove("diq-v600-scanning");
    }

    /*
      Retire any externally-created full-width SCANNING control that survived
      the normal UI transition. Match only an exact scanning label so unrelated
      loading surfaces are untouched.
    */
    try {
      nodes = document.querySelectorAll(
        "button,[role='button'],[data-diq-scan-control]," +
        "[data-adventure-scan-control],.diqScanControl,.adventureScanControl"
      );
    } catch (error) {
      nodes = [];
    }

    Array.prototype.forEach.call(nodes, function (node) {
      var label = String(
        node && (
          node.textContent ||
          node.getAttribute("aria-label") ||
          ""
        ) || ""
      ).replace(/\s+/g, " ").trim().toUpperCase();

      if (!/^SCANNING(?:\.\.\.|…)?$/.test(label)) {
        return;
      }

      node.classList.remove("loading");
      node.classList.remove("scanning");
      node.classList.remove("is-loading");
      node.classList.remove("is-scanning");
      node.removeAttribute("disabled");
      node.setAttribute("aria-disabled", "false");
      node.setAttribute("aria-hidden", "true");
      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("opacity", "0", "important");
      node.style.setProperty("pointer-events", "none", "important");
      node.setAttribute("data-diq-stale-scanning-retired", source);
    });

    return true;
  }

  function settleCommittedResultsUI(payload, reason) {
    var supplied = payload || {};
    var results = Array.isArray(supplied.results)
      ? supplied.results
      : [];
    var count = Number(
      supplied.count ||
      results.length ||
      state.get("results.count", 0) ||
      0
    );

    if (count <= 0) {
      return false;
    }

    releaseStaleScanningSurfaces(reason || "committed-results");
    destinationEditingActive = false;
    unlockDestinationChooser(reason || "committed-results");
    showMapResultsShell(reason || "committed-results");
    restoreMapInteractionAuthority(
      reason || "committed-results"
    );
    showResultsPill(count);
    showScanAgainControl();
    holdResultsPanelAuthority(
      reason || "committed-results"
    );

    /*
      Run two bounded cleanup passes because an older control module may paint
      its SCANNING button one frame after result commit.
    */
    window.setTimeout(function () {
      releaseStaleScanningSurfaces(
        String(reason || "committed-results") + "-post-frame"
      );
    }, 0);

    window.setTimeout(function () {
      releaseStaleScanningSurfaces(
        String(reason || "committed-results") + "-post-layout"
      );
    }, 180);

    return true;
  }


  function bindEvents() {
    unsubscribers.push(
      events.on("scan:started", function (event) {
        var payload = event.payload || {};

        if (continuationActive(payload)) {
          keepContinuationResultsVisible(
            "scan-started-continuation"
          );
          return;
        }

        scanCompleted = false;
        enterScanningState(payload);
      })
    );

    unsubscribers.push(
      events.on("scan:progress", function (event) {
        var payload = event.payload || {};

        if (continuationActive(payload)) {
          keepContinuationResultsVisible(
            "scan-progress-continuation"
          );
          return;
        }

        if (!scanningModeActive()) {
          enterScanningState(payload);
        }

        updateScanProgress(payload);
        hideScanningConflicts();
      })
    );

    unsubscribers.push(
      events.on("recommendations:continuation-started", function (event) {
        var payload = event && event.payload || {};

        calmContinuationActive = true;
        calmContinuationToken = String(payload.token || "");

        keepContinuationResultsVisible(
          "continuation-started"
        );
      })
    );

    unsubscribers.push(
      events.on("recommendations:continuation-settled", function () {
        settleCalmContinuation(
          "continuation-settled"
        );
      })
    );

    unsubscribers.push(
      events.on("recommendations:refill-completed", function () {
        hideScanStatus(0);
      })
    );

    unsubscribers.push(
      events.on("recommendations:exhausted", function () {
        settleCalmContinuation(
          "continuation-exhausted"
        );
      })
    );

    unsubscribers.push(
      events.on("results:committed", function (event) {
        settleCommittedResultsUI(
          event && event.payload || {},
          "results-committed"
        );
      })
    );

    unsubscribers.push(
      events.on("scan:settled", function (event) {
        var payload = event && event.payload || {};

        if (
          String(payload.status || "") === "completed" &&
          Number(
            payload.count ||
            state.get("results.count", 0) ||
            0
          ) > 0
        ) {
          settleCommittedResultsUI(
            payload,
            "scan-settled"
          );
        }
      })
    );

    unsubscribers.push(
      events.on("scan:completed", function (event) {
        var payload = event.payload || {};
        var results = payload.results || [];
        var count = results.length ||
          Number(state.get("results.count", 0) || 0);

        calmContinuationActive = false;
        calmContinuationToken = "";
        scanCompleted = true;
        exitScanningState();
        destinationEditingActive = false;
        unlockDestinationChooser("scan-completed");
        scheduleDestinationFieldSync("scan-completed");
        hideScanStatus(0);

        /*
          Map-First Results Shell:
          Scan V600.55 intentionally keeps Top Picks closed. Restore the
          Adventure panel, map pins, and Places control instead of scheduling
          shelf restoration passes that can leave the user with a bare map.
        */
        showMapResultsShell("scan-completed");

        if (count > 0) {
          settleCommittedResultsUI(
            {
              results: results,
              count: count
            },
            "scan-completed"
          );
        } else {
          hideResultsPill();
          hideScanAgainControl();
        }

        ensureAdventureCategoryDock();
        endCategoryRescanPresentation("scan-completed");
        refreshCategoryDockVisibility("scan-completed");

        showToast(
          count
            ? count +
              (count === 1
                ? " place found"
                : " places found")
            : "No strong places found"
        );
      })
    );

    unsubscribers.push(
      events.on("scan:failed", function (event) {
        if (continuationActive(event && event.payload || {})) {
          settleCalmContinuation(
            "continuation-failed"
          );
          return;
        }

        scanCompleted = false;
        exitScanningState();
        endCategoryRescanPresentation("scan-failed");
        lockDestinationChooser("scan-completed");
        setDisplay(get(ids.scanOverlay), false);

        if (browseModeActive()) {
          enforceResultsBrowseState();
          showOnlyResultShelf();
        } else {
          showMenu();
        }

        showToast(
          event &&
          event.payload &&
          event.payload.message
            ? event.payload.message
            : "Adventure scan failed"
        );
      })
    );

    unsubscribers.push(
      events.on("scan:cancelled", function () {
        scanCompleted = false;
        exitScanningState();
        endCategoryRescanPresentation("scan-cancelled");
        lockDestinationChooser("scan-completed");
        setDisplay(get(ids.scanOverlay), false);

        if (browseModeActive()) {
          enforceResultsBrowseState();
          showOnlyResultShelf();
          return;
        }

        showMenu();
      })
    );
    unsubscribers.push(
      events.on("results:shelf-shown", function () {
        if (destinationEditingActive) {
          hideAllResultShelves();
          hideResultsPill();
          return;
        }

        if (
          scanningModeActive() &&
          !scanCompleted
        ) {
          hideAllResultShelves();
          return;
        }

        showResultsPill(
          state.get("results.count", 0)
        );
        holdMapTouchAuthority("results-shelf-shown");

        if (activePlaceItem) {
          reassertGoldPin("results-shelf-shown");
        }
      })
    );

    unsubscribers.push(
      events.on("results:shelf-hidden", function (event) {
        var payload = event && event.payload ? event.payload : {};
        restoreResultSessionControls("results-shelf-hidden");
        holdMapTouchAuthority("results-shelf-hidden");
        var reason = String(payload.reason || "");

        if (
          !placeIsOpen() &&
          (
            reason === "user-close" ||
            payload.userClosed === true ||
            reason.indexOf("places-button") === 0
          )
        ) {
          browseResultsActive = false;
          stopBrowseGuard();
          showMapResultsShell("results-shelf-hidden");
        }
      })
    );

    unsubscribers.push(
      events.on("results:shelf-minimized", function (event) {
        var payload = event && event.payload ? event.payload : {};

        if (
          state.get("results.count", 0) > 0 &&
          !placeIsOpen() &&
          !destinationEditingActive
        ) {
          browseResultsActive = false;
          stopBrowseGuard();
          showMapResultsShell("results-shelf-minimized");
        }
      })
    );

    unsubscribers.push(
      events.on("pins:committed", function () {
        if (activePlaceItem) {
          reassertGoldPin("pins-committed");
        }
      })
    );

    unsubscribers.push(
      events.on("results:rendered", function () {
        if (activePlaceItem) {
          reassertGoldPin("results-rendered");
        }
      })
    );

    unsubscribers.push(
      events.on("map:ready", function () {
        if (activePlaceItem) {
          reassertGoldPin("map-ready");
        }
      })
    );

    unsubscribers.push(
      events.on("map:style-ready", function () {
        if (activePlaceItem) {
          reassertGoldPin("map-style-ready");
        }
      })
    );


    unsubscribers.push(
      events.on("pin:selected", function (event) {
        var item = selectionItem(event);

        if (item) {
          activePlaceItem = item;
          openSelectedPlace(item, "pin");
          return;
        }

        hideAllResultShelves();

        if (state.get("results.count", 0) > 0) {
          showResultsPill(
            state.get("results.count", 0)
          );
        }

        if (document.body) {
          document.body.classList.add("diqPlaceIQMode");
        }
      })
    );

    unsubscribers.push(
      events.on("place:opened", function (event) {
        clearPlaceOpenWatchdog();

        var item =
          selectionItem(event) ||
          selectedPlaceFromState();

        browseResultsActive = false;
        openingPlaceKey = "";

        if (openingPlaceTimer) {
          window.clearTimeout(openingPlaceTimer);
          openingPlaceTimer = null;
        }

        if (item) {
          activePlaceItem = item;
          focusSelectedPlace(item, "place-opened");
          persistSelectedPinHighlight(item);
          reassertGoldPin("place-opened");
        }

        showPlace();
        refreshCategoryDockVisibility("place-opened");
      })
    );

    unsubscribers.push(
      events.on("place:open-failed", function (event) {
        var payload = event && event.payload ? event.payload : {};
        var item =
          payload.item ||
          activePlaceItem ||
          selectedPlaceFromState();

        restoreAfterFailedPlaceOpen(
          item,
          payload.source || "place-card",
          payload.message || "place-open-failed"
        );
      })
    );

    unsubscribers.push(
      events.on("place:closed", function () {
        clearPlaceOpenWatchdog();
        window.setTimeout(function () {
          restoreResultSessionControls("place-closed");
          holdMapTouchAuthority("place-closed");
        }, 0);
        openingPlaceKey = "";

        if (openingPlaceTimer) {
          window.clearTimeout(openingPlaceTimer);
          openingPlaceTimer = null;
        }

        clearPinHighlightTimers();

        if (document.body) {
          document.body.classList.remove("diqPlaceIQMode");
        }

        browseResultsActive = true;
        enforceResultsBrowseState();

        window.setTimeout(function () {
          releaseResultShelfOverrides();
          restorePersistentShelf("place-closed");

          if (activePlaceItem) {
            persistSelectedPinHighlight(activePlaceItem);
            reassertGoldPin("place-closed-results-restored");
          }

          resultsReturnInProgress = false;
          refreshCategoryDockVisibility("place-closed");
        }, 40);
      })
    );

    unsubscribers.push(
      events.on("destination:committed", function (event) {
        var payload = event && event.payload ? event.payload : {};
        var destination =
          payload.destination ||
          payload.item ||
          payload.result ||
          payload;

        endDestinationEditing("destination-committed", {
          restoreCommittedLabel: false
        });

        if (destination && typeof destination === "object") {
          lastCommittedDestinationLabel = String(
            destination.canonicalLabel ||
            destination.displayName ||
            destination.full ||
            destination.label ||
            destination.name ||
            destination.formattedAddress ||
            destination.address ||
            lastCommittedDestinationLabel ||
            ""
          ).replace(/\s+/g, " ").trim();
        }

        lockDestinationChooser("destination-committed");
        syncDestinationField("destination-committed", true);
      })
    );

    unsubscribers.push(
      events.on("destination:cleared", function () {
        scanCompleted = false;
        destinationEditingActive = false;
        lastCommittedDestinationLabel = "";

        if (document.body) {
          document.body.classList.remove("diq-v600-destination-editing");
        }

        unlockDestinationChooser("destination-cleared");
        browseResultsActive = false;
        stopBrowseGuard();
        showMenu({ force: true });
      })
    );

    unsubscribers.push(
      events.on("route:failed", function (event) {
        showToast(
          event &&
          event.payload &&
          event.payload.message
            ? event.payload.message
            : "Route could not start"
        );
      })
    );

    unsubscribers.push(
      events.on("map:restored", function () {
        var mode = state.get("ui.mode", "menu");

        restoreResultSessionControls("map-restored");
        holdMapTouchAuthority("map-restored");

        if (browseModeActive()) {
          enforceResultsBrowseState();
          showOnlyResultShelf();

          if (activePlaceItem) {
            persistSelectedPinHighlight(activePlaceItem);
            reassertGoldPin("map-restored-browse");
          }

          return;
        }

        if (placeIsOpen()) {
          showPlace();
          return;
        }

        renderMode(mode);
      })
    );
  }

  function endCategoryRescanPresentation(reason){
    categoryRescanActive=false;
    categoryRescanFilter="";
    categoryRescanLabel="";

    if(categoryRescanFallbackTimer){
      window.clearTimeout(categoryRescanFallbackTimer);
      categoryRescanFallbackTimer=null;
    }

    if(document.body){
      document.body.classList.remove("diq-v600-category-rescan");
    }

    try{
      events.emit("ui:category-rescan-presentation-ended",{
        reason:String(reason||"settled")
      });
    }catch(error){}

    return true;
  }

  function beginCategoryRescanPresentation(filter,label){
    categoryRescanActive=true;
    categoryRescanFilter=String(filter||"");
    categoryRescanLabel=String(label||"").trim() ||
      String(filter||"places").replace(/_/g," ");

    if(categoryRescanFallbackTimer){
      window.clearTimeout(categoryRescanFallbackTimer);
    }

    /* Defensive escape hatch only; normal scan lifecycle events clear this. */
    categoryRescanFallbackTimer=window.setTimeout(function(){
      if(!categoryRescanActive){return;}
      endCategoryRescanPresentation("fallback-timeout");
      hideScanStatus(0);
      refreshCategoryDockVisibility("category-rescan-timeout");
    },20000);

    if(document.body){
      document.body.classList.add("diq-v600-category-rescan");
    }

    forceCloseAdventureMenuSurface("category-rescan-requested");
    minimizePersistentShelf("category-rescan-requested");
    hideResultsPill();
    hideScanAgainControl();
    showScanStatus({message:categoryRescanStatusMessage()});
    refreshCategoryDockVisibility("category-rescan-requested");

    try{
      events.emit("ui:category-rescan-presentation-started",{
        category:categoryRescanFilter,
        label:categoryRescanLabel
      });
    }catch(error){}

    return true;
  }

  function categoryDockPriority(filter){
    var order=[
      "local_favorites","food_iq","coffee","outdoors","hidden_gems",
      "trending","live_music","waterfront","lake_iq","sunset_iq",
      "family_iq","dog_iq","gameday","nightlife"
    ];
    var index=order.indexOf(String(filter||""));
    return index<0?999:index;
  }

  function syncCategoryDockSelection(){
    var dock=get(ids.categoryDock);
    var originals=document.querySelectorAll("#adventureChips .chip");
    var selectedFilter="";
    var index;

    for(index=0;index<originals.length;index+=1){
      if(originals[index].classList.contains("on")){
        selectedFilter=String(originals[index].getAttribute("data-filter")||"");
        break;
      }
    }

    if(dock){
      Array.prototype.forEach.call(
        dock.querySelectorAll(".diqAdventureDockChip"),
        function(chip){
          chip.classList.toggle(
            "on",
            String(chip.getAttribute("data-filter")||"")===selectedFilter
          );
        }
      );
    }
    return selectedFilter;
  }

  function refreshCategoryDockVisibility(reason){
    var dock=get(ids.categoryDock);
    var count=Number(state.get("results.count",0)||0);
    var visible=!!(
      dock&&
      count>0&&
      (!scanningModeActive()||categoryRescanActive)&&
      !destinationEditingActive&&
      !placeIsOpen()&&
      !independentMenuOpen
    );

    if(!dock){return false;}
    dock.setAttribute("data-visible",visible?"true":"false");
    dock.setAttribute("aria-hidden",visible?"false":"true");
    dock.setAttribute("data-rescanning",categoryRescanActive?"true":"false");
    dock.style.pointerEvents=(visible&&!categoryRescanActive)?"auto":"none";
    syncCategoryDockSelection();

    try{
      events.emit("ui:category-dock-visibility",{
        visible:visible,
        reason:String(reason||"refresh")
      });
    }catch(error){}

    return visible;
  }

  function ensureAdventureCategoryDock(){
    var source=document.getElementById("adventureChips");
    var dock=get(ids.categoryDock);
    var originals=[];

    if(!source){return null;}

    if(!dock){
      dock=document.createElement("div");
      dock.id=ids.categoryDock;
      dock.setAttribute("aria-label","Adventure discovery categories");
      dock.setAttribute("aria-hidden","true");
      dock.setAttribute("data-visible","false");
      document.body.appendChild(dock);
    }

    originals=Array.prototype.slice.call(source.querySelectorAll(".chip"));
    originals.sort(function(a,b){
      return categoryDockPriority(a.getAttribute("data-filter"))-
        categoryDockPriority(b.getAttribute("data-filter"));
    });

    if(!dock.__diqCategoryDockBuilt){
      originals.forEach(function(original){
        var clone=document.createElement("button");
        var filter=String(original.getAttribute("data-filter")||"");
        clone.type="button";
        clone.className="diqAdventureDockChip";
        clone.textContent=String(original.textContent||"").trim();
        clone.setAttribute("data-filter",filter);
        clone.setAttribute("aria-label","Explore "+String(original.textContent||"").trim());

        clone.addEventListener("click",function(event){
          event.preventDefault();
          event.stopPropagation();

          var live=document.querySelector(
            "#adventureChips .chip[data-filter='"+
            filter.replace(/'/g,"\\'")+
            "']"
          );

          if(live){
            beginCategoryRescanPresentation(
              filter,
              String(original.textContent||"").trim()
            );
            live.click();
            window.setTimeout(function(){
              syncCategoryDockSelection();
              refreshCategoryDockVisibility("category-switch");
            },40);
          }
        },false);

        dock.appendChild(clone);

        if(!original.__diqCategoryDockSyncBound){
          original.addEventListener("click",function(){
            window.setTimeout(syncCategoryDockSelection,20);
          },false);
          original.__diqCategoryDockSyncBound=true;
        }
      });

      dock.__diqCategoryDockBuilt=true;
    }

    refreshCategoryDockVisibility("ensure");
    return dock;
  }

  function init() {
    if (initialized) {
      renderMode(
        state.get("ui.mode", "menu")
      );
      return true;
    }

    ensureStyles();
    ensurePersistentNavigationControls();
    ensureAdventureCategoryDock();
    ensureResultsPill();
    hideScanAgainControl();
    ensureScanStatus();
    scheduleLegacyControlRetirement();

    var resultsPillControl = ensureResultsPill();

    if (!resultsPillControl.__diqIndependentToggleBound) {
      /*
       * Replace the node once to strip stale listeners from older UI builds,
       * then bind one independent open/close toggle.
       */
      if (resultsPillControl.parentNode) {
        var cleanResultsPill = resultsPillControl.cloneNode(true);
        resultsPillControl.parentNode.replaceChild(
          cleanResultsPill,
          resultsPillControl
        );
        resultsPillControl = cleanResultsPill;
      }

      resultsPillControl.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }

        toggleResultsShelf("places-button-independent");

        if (Number(state.get("results.count", 0) || 0) > 0) {
          showResultsPill(state.get("results.count", 0));
        }
      }, true);

      resultsPillControl.__diqIndependentToggleBound = true;
      resultsPillControl.setAttribute("aria-label", "Open or close places");
      resultsPillControl.setAttribute("title", "Open or close places");
      resultsPillControl.setAttribute("role", "button");
    }

    hideLegacyShelf();
    bindEvents();
    schedulePremiumResultShelfDecoration("ui-init");
if (!document.__diqBrowseGuardBound) {
      document.addEventListener("click", function (event) {
        var target;

        if (!browseModeActive() && !scanningModeActive()) {
          return;
        }

        target = event.target && event.target.closest
          ? event.target.closest(
              "#diqAdventureMenu,#diqAdventureSearchResults," +
              "#diqV500SearchPanel,#diqAdventureScanTab," +
              "[data-diq-destination-results]," +
              ".diqDestinationResults,.diqSearchResults," +
              ".adventureSearchResults," +
              "[data-search-results],[data-autocomplete-results]," +
              "[data-location-suggestions]," +
              "[class*='autocomplete'],[class*='destination-results']"
            )
          : null;

        if (target) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (scanningModeActive()) {
            hideScanningConflicts();
          } else {
            enforceResultsBrowseState();
          }
        }
      }, true);

      document.__diqBrowseGuardBound = true;
    }

    if (!document.__diqDestinationFieldAuthorityBound) {
      document.addEventListener("focusin", function (event) {
        var target = event.target;

        if (
          target &&
          destinationInputFields().indexOf(target) >= 0
        ) {
          if (!destinationEditingActive) {
            beginDestinationEditing("destination-field-focus");
          } else {
            destinationChooserLocked = false;
            stopDestinationChooserLock();
            if (Number(state.get("results.count", 0) || 0) > 0) {
              showResultsPill(state.get("results.count", 0));
            }
            hideAllResultShelves();
          }
        }
      }, true);

      document.addEventListener("input", function (event) {
        var target = event.target;

        if (
          target &&
          destinationInputFields().indexOf(target) >= 0
        ) {
          destinationEditingActive = true;
          destinationChooserLocked = false;
          stopDestinationChooserLock();
          stopBrowseGuard();
          if (Number(state.get("results.count", 0) || 0) > 0) {
            showResultsPill(state.get("results.count", 0));
          }
          hideAllResultShelves();

          if (document.body) {
            document.body.classList.add("diq-v600-destination-editing");
          }

          try {
            if (
              window.DriveIQAdventureDestination &&
              typeof window.DriveIQAdventureDestination.updateEditQuery === "function"
            ) {
              window.DriveIQAdventureDestination.updateEditQuery(
                target.value,
                "destination-field-input"
              );
            }
          } catch (error) {}
        }
      }, true);

      document.addEventListener("focusout", function (event) {
        var target = event.target;

        if (
          target &&
          destinationInputFields().indexOf(target) >= 0
        ) {
          window.setTimeout(function () {
            var active = document.activeElement;
            var destinationUI = active && active.closest
              ? active.closest(
                  "#diqAdventureMenu,#diqAdventureSearchResults," +
                  "#diqV500SearchPanel,[data-diq-destination-results]," +
                  "[data-search-results],[data-autocomplete-results]," +
                  "[data-location-suggestions],[role='listbox']"
                )
              : null;

            if (
              destinationInputFields().indexOf(active) < 0 &&
              !destinationUI
            ) {
              /* Keep the typed query intact. Commit/cancel owns edit exit. */
            }
          }, 80);
        }
      }, true);

      document.__diqDestinationFieldAuthorityBound = true;
    }

    var panelMinimize = get("adventurePanelMinimize");

    if (panelMinimize && !panelMinimize.__diqTouchReleaseBound) {
      panelMinimize.addEventListener("click", function () {
        /*
         * Let any existing minimize handler run first, then enforce the real
         * wrapper close and return touch authority to Mapbox and pin markers.
         */
        window.setTimeout(function () {
          forceCloseAdventureMenuSurface("panel-minimize");
          independentMenuOpen = false;
          var persistentDestinations = get(ids.persistentDestinations);
          if (persistentDestinations) {
            persistentDestinations.setAttribute("aria-expanded", "false");
          }
          if (Number(state.get("results.count", 0) || 0) > 0) {
            showResultsPill(state.get("results.count", 0));
          }
        }, 0);
      }, false);

      panelMinimize.__diqTouchReleaseBound = true;
    }

    scheduleDestinationFieldSync("init");

    initialized = true;

    renderMode(
      state.get("ui.mode", "menu")
    );

    log("Controller initialized");

    return true;
  }

  function destroy() {
    var index;

    for (index = 0; index < unsubscribers.length; index += 1) {
      try {
        unsubscribers[index]();
      } catch (error) {}
    }

    unsubscribers.length = 0;

    if (toastTimer) {
      window.clearTimeout(toastTimer);
      toastTimer = null;
    }

    if (progressTimer) {
      window.clearInterval(progressTimer);
      progressTimer = null;
    }

    if (openingPlaceTimer) {
      window.clearTimeout(openingPlaceTimer);
      openingPlaceTimer = null;
    }

    clearPlaceOpenWatchdog();

    if (legacyControlTimer) {
      window.clearTimeout(legacyControlTimer);
      legacyControlTimer = null;
    }

    if (scanStatusTimer) {
      window.clearTimeout(scanStatusTimer);
      scanStatusTimer = null;
    }

    scanStatusVisible = false;
    scanningActive = false;
    scanCompleted = false;
    destinationChooserLocked = false;
    stopDestinationChooserLock();
    stopBrowseGuard();
    clearPinHighlightTimers();
    clearGoldReassertTimers();
    clearResultsReturnTimers();
    resultsReturnInProgress = false;
    browseResultsActive = false;
    activePlaceItem = null;
    openingPlaceKey = "";
    initialized = false;

    return true;
  }

  window.DriveIQAdventureUI = {
    init: init,
    destroy: destroy,
    renderMode: renderMode,
    showMenu: showMenu,
    showScanning: showScanning,
    showMap: showMap,
    showPlace: showPlace,
    showToast: showToast,
    showScanStatus: showScanStatus,
    updateScanStatus: updateScanStatus,
    hideScanStatus: hideScanStatus,
    enterScanningState: enterScanningState,
    exitScanningState: exitScanningState,
    scanningModeActive: scanningModeActive,
    hideScanningConflicts: hideScanningConflicts,
    showResultsPill: showResultsPill,
    hideResultsPill: hideResultsPill,
    restoreResultSessionControls: restoreResultSessionControls,
    ensurePersistentNavigationControls: ensurePersistentNavigationControls,
    ensureAdventureCategoryDock: ensureAdventureCategoryDock,
    refreshCategoryDockVisibility: refreshCategoryDockVisibility,
    beginCategoryRescanPresentation: beginCategoryRescanPresentation,
    endCategoryRescanPresentation: endCategoryRescanPresentation,
    syncCategoryDockSelection: syncCategoryDockSelection,
    forceOpenAdventureMenuSurface: forceOpenAdventureMenuSurface,
    forceCloseAdventureMenuSurface: forceCloseAdventureMenuSurface,
    restoreBottomSwipeMenu: restoreBottomSwipeMenu,
    showScanAgainControl: showScanAgainControl,
    hideScanAgainControl: hideScanAgainControl,
    openScanAgain: openScanAgain,
    hideLegacyShelf: hideLegacyShelf,
    hideAllResultShelves: hideAllResultShelves,
    releaseResultShelfOverrides: releaseResultShelfOverrides,
    minimizePersistentShelf: minimizePersistentShelf,
    restorePersistentShelf: restorePersistentShelf,
    closePersistentShelf: closePersistentShelf,
    placeIsOpen: placeIsOpen,
    browseModeActive: browseModeActive,
    reopenResultsShelf: reopenResultsShelf,
    toggleResultsShelf: toggleResultsShelf,
    showMapResultsShell: showMapResultsShell,
    collapseAdventureResultsPanel: collapseAdventureResultsPanel,
    releaseAdventureResultsPanel: releaseAdventureResultsPanel,
    releaseViewportTouchBlockers: releaseViewportTouchBlockers,
    holdMapTouchAuthority: holdMapTouchAuthority,
    holdResultsPanelAuthority: holdResultsPanelAuthority,
    continuationActive: continuationActive,
    keepContinuationResultsVisible: keepContinuationResultsVisible,
    settleCalmContinuation: settleCalmContinuation,
    showOnlyResultShelf: showOnlyResultShelf,
    enforceResultsBrowseState: enforceResultsBrowseState,
    startBrowseGuard: startBrowseGuard,
    stopBrowseGuard: stopBrowseGuard,
    suppressDestinationChooserOverlays: suppressDestinationChooserOverlays,
    lockDestinationChooser: lockDestinationChooser,
    unlockDestinationChooser: unlockDestinationChooser,
    beginDestinationEditing: beginDestinationEditing,
    endDestinationEditing: endDestinationEditing,
    syncDestinationField: syncDestinationField,
    destinationLabelFromState: destinationLabelFromState,
    openSelectedPlace: openSelectedPlace,
    placeCardVisiblyOpen: placeCardVisiblyOpen,
    restoreAfterFailedPlaceOpen: restoreAfterFailedPlaceOpen,
    focusSelectedPlace: focusSelectedPlace,
    applySelectedPinHighlight: applySelectedPinHighlight,
    persistSelectedPinHighlight: persistSelectedPinHighlight,
    reassertGoldPin: reassertGoldPin,
    retireLegacyResultsControls: retireLegacyResultsControls,
    scheduleLegacyControlRetirement: scheduleLegacyControlRetirement,
    status: function () {
      return {
        build: "V604_5_MINIMIZE_TOUCH_RELEASE",
        initialized: initialized,
        mode: currentMode(),
        placeOpen: placeIsOpen(),
        activePlaceKey: activePlaceItem
          ? placeKey(activePlaceItem)
          : "",
        pinHighlightTimerCount: pinHighlightTimers.length,
        goldReassertTimerCount: goldReassertTimers.length,
        resultsReturnInProgress: resultsReturnInProgress,
        browseResultsActive: browseResultsActive,
        browseGuardActive: !!browseGuardTimer,
        browseGuardIntervalMs: BROWSE_GUARD_INTERVAL_MS,
        scanStatusVisible: scanStatusVisible,
        scanningActive: scanningActive,
        scanCompleted: scanCompleted,
        destinationChooserLocked: destinationChooserLocked,
      destinationEditingActive: destinationEditingActive,
        destinationChooserLockActive: !!destinationChooserLockTimer,
        placeOpenWatchdogActive: !!placeOpenWatchdogTimer,
        placeOpenWatchdogMs: PLACE_OPEN_WATCHDOG_MS,
        placeCardVisiblyOpen: placeCardVisiblyOpen(),
        cameraVerticalOffset: CAMERA_VERTICAL_OFFSET,
        resultsReturnTimerCount: resultsReturnTimers.length,
        resultShelfCount: resultShelfNodes().length,
        categoryDockReady: !!get(ids.categoryDock),
        categoryDockVisible: !!(get(ids.categoryDock)&&get(ids.categoryDock).getAttribute("data-visible")==="true"),
        scanAgainVisible: !!(
          get(ids.scanAgain) &&
          get(ids.scanAgain).classList.contains("on")
        )
      };
    }
  };

  log("V605.3 persistent discovery category dock loaded");
}(window, document));